import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import SharedPageLayout from './SharedPageLayout';
import SectionModal from './SectionModal';
import { navigationItems } from '../data/navigation';
import { theme } from '../theme';

// Type pour les items avec sections optionnelles
interface TraditionItem {
  id: string;
  name: string;
  image?: string;
  sections?: { [key: string]: string };
}

const TraditionMainSection = styled.section`
  width: 100%;
  padding: 20px 5% 80px 5%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 15px 4% 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 10px 3% 40px 3%;
  }
`;

const TraditionMainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-x: hidden;

  @media (max-width: 768px) {
    gap: 0;
  }

  @media (max-width: 480px) {
    gap: 0;
  }
`;

const ConstructionBanner = styled.p`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 42px;
  text-align: center;
  margin: 0 0 40px 0;
  width: 100%;
  background: linear-gradient(90deg, ${theme.colors.outchou_green} 0%, ${theme.colors.outchou_yellow} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const SectionTitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  padding-right: 50px;

  @media (max-width: 480px) {
    padding-right: 45px;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  color: ${theme.colors.outchou_blue};
  text-align: left;
  margin: 0 0 30px 0;

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
    margin-bottom: 15px;
  }
`;

const ScrollIndicator = styled.div`
  display: none;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  pointer-events: none;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    right: 6px;
  }

  svg {
    width: 100%;
    height: 100%;
    stroke: ${theme.colors.outchou_blue};
  }
`;

const CategoriesCarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
  overflow: hidden;

  .swiper {
    padding-right: 60px;
    overflow: visible;
    
    @media (max-width: 768px) {
      padding-right: 0;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
`;

const CategoryCard = styled.div`
  background: transparent;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  transform: scale(1);
  width: 280px;
  position: relative;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.01);
  }

  @media (min-width: 480px) {
    width: 300px;
  }

  @media (min-width: 768px) {
    width: 380px;
  }
`;

const CategoryImage = styled.div<{ $categoryColor?: string; imageUrl?: string }>`
  width: 100%;
  height: 400px;
  background-image: ${props => props.imageUrl 
    ? `url("${props.imageUrl}")` 
    : `linear-gradient(135deg, ${props.$categoryColor || theme.colors.outchou_green} 0%, ${props.$categoryColor ? `${props.$categoryColor}CC` : theme.colors.outchou_blue} 100%)`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0) 100%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 350px;
    padding: 24px;
  }

  @media (max-width: 480px) {
    height: 300px;
    padding: 20px;
  }
`;

const CategoryContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const CategoryTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 32px;
  color: white;
  margin: 0 0 8px 0;
  text-align: left;
  letter-spacing: -0.5px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 26px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const CategoryCount = styled.span`
  font-family: ${theme.fonts.body};
  font-size: 12px;
  color: white;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 6px 12px;
  border-radius: 0;
  display: inline-block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin-top: 8px;
`;

const CategoryNavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 10px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CategoryNavButton = styled.button<{ $disabled?: boolean }>`
  background: transparent;
  color: ${theme.colors.outchou_green};
  border: none;
  width: 80px;
  height: 80px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: 400;
  transition: opacity 0.2s ease;
  padding: 0;
  opacity: ${props => props.$disabled ? 0.2 : 1};

  &:hover {
    opacity: ${props => props.$disabled ? 0.2 : 0.7};
  }

  &:active {
    opacity: ${props => props.$disabled ? 0.2 : 0.5};
  }
`;

const ItemsSection = styled.div`
  margin-top: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  position: relative;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 0;
  font-family: ${theme.fonts.body};
  font-size: 12px;
  position: relative;
  z-index: 10000;
`;

const BreadcrumbItem = styled.span<{ $isClickable?: boolean }>`
  color: ${props => props.$isClickable ? theme.colors.outchou_blue : theme.colors.gray};
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  font-weight: ${props => props.$isClickable ? '400' : '300'};
  transition: all 0.2s ease;
  opacity: ${props => props.$isClickable ? '1' : '0.6'};
  
  &:hover {
    color: ${props => props.$isClickable ? theme.colors.outchou_green : theme.colors.gray};
    opacity: ${props => props.$isClickable ? '1' : '0.6'};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${theme.colors.gray};
  font-weight: 300;
  opacity: 0.4;
`;

const CategoryNote = styled.p`
  font-family: ${theme.fonts.body};
  font-size: 14px;
  line-height: 1.5;
  color: ${theme.colors.gray};
  margin: 0 0 16px 0;
  text-align: left;

  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const ItemsCarouselWrapper = styled.div<{ $categoryColor?: string }>`
  position: relative;
  width: 100%;
  padding: 0;
  margin-bottom: 0;
  overflow: hidden;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 0;
  }

  .swiper {
    padding-right: 60px;
    overflow: visible;
    
    @media (max-width: 768px) {
      padding-right: 0;
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
`;

const ItemsNavigationButtons = styled.div<{ $categoryColor?: string }>`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 10px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ItemNavButton = styled.button<{ $disabled?: boolean; $categoryColor?: string }>`
  background: transparent;
  color: ${props => props.$categoryColor || theme.colors.outchou_green};
  border: none;
  width: 80px;
  height: 80px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: 400;
  transition: opacity 0.2s ease;
  padding: 0;
  opacity: ${props => props.$disabled ? 0.2 : 1};

  &:hover {
    opacity: ${props => props.$disabled ? 0.2 : 0.7};
  }

  &:active {
    opacity: ${props => props.$disabled ? 0.2 : 0.5};
  }
`;

const ItemCard = styled.div`
  width: auto;
  background: transparent;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ItemImage = styled.div<{ imageUrl?: string; $categoryColor?: string }>`
  width: 250px;
  height: 280px;
  background-image: ${props => props.imageUrl 
    ? `url("${props.imageUrl}"), linear-gradient(135deg, ${props.$categoryColor || theme.colors.outchou_green} 0%, ${props.$categoryColor ? `${props.$categoryColor}CC` : theme.colors.outchou_blue} 100%)`
    : `linear-gradient(135deg, ${props.$categoryColor || theme.colors.outchou_green} 0%, ${props.$categoryColor ? `${props.$categoryColor}CC` : theme.colors.outchou_blue} 100%)`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    width: 300px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0) 100%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 240px;
    padding: 16px;
  }
`;

const ItemContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const ItemName = styled.h4`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 20px;
  color: white;
  margin: 0;
  text-align: left;
  letter-spacing: -0.3px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const TraditionPage: React.FC = () => {
  const traditionData = navigationItems.find(item => item.id === 'tradition');
  const categorySwiperRef = useRef<SwiperType | null>(null);
  const artisanatSwiperRef = useRef<SwiperType | null>(null);
  const musiqueSwiperRef = useRef<SwiperType | null>(null);
  const traditionItemsSwiperRef = useRef<SwiperType | null>(null);
  const artisanatItemsSwiperRef = useRef<SwiperType | null>(null);
  const itemsSwiperRef = useRef<SwiperType | null>(null);
  const instrumentsSubcategorySwiperRef = useRef<SwiperType | null>(null);
  const instrumentsItemsSwiperRef = useRef<SwiperType | null>(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [selectedTraditionCategory, setSelectedTraditionCategory] = useState<string | null>(null);
  const [selectedCostumeFemininCategory, setSelectedCostumeFemininCategory] = useState<string | null>(null);
  const [selectedCostumeMasculinCategory, setSelectedCostumeMasculinCategory] = useState<string | null>(null);
  const [selectedBijouxCategory, setSelectedBijouxCategory] = useState<string | null>(null);
  const [selectedTenuesDisparuesCategory, setSelectedTenuesDisparuesCategory] = useState<string | null>(null);
  const [selectedArtisanatCategory, setSelectedArtisanatCategory] = useState<string | null>(null);
  const [selectedMusiqueCategory, setSelectedMusiqueCategory] = useState<string | null>(null);
  const [selectedInstrumentsSubcategory, setSelectedInstrumentsSubcategory] = useState<string | null>(null);
  const [selectedMusiquesChantSubcategory, setSelectedMusiquesChantSubcategory] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<TraditionItem | null>(null);
  const [selectedItemColor, setSelectedItemColor] = useState<string>(theme.colors.outchou_green);

  const traditionCategories = [
    { id: 'costume-feminin', name: 'Costume féminin', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/vetements/costume-feminin/costume-feminin-cover.jpeg' },
    { id: 'costume-masculin', name: 'Costume masculin', color: theme.colors.outchou_blue, image: '/shared_assets/images/tradition/vetements/costume-masculin/costume-masculin-cover.jpeg' },
    { id: 'bijoux-accessoires', name: 'Bijoux & accessoires', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/bijoux/fibule/jbine.jpeg' },
    { id: 'tenues-disparues', name: 'Éléments disparus', color: theme.colors.outchou_blue, image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/aquaran.jpeg' },
  ];

  const artisanatCategories = [
    { id: 'tapisserie', name: 'Tapisserie', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/artisanat/tapisserie/zerbiya-chaouia.jpeg' },
    { id: 'travail-metal', name: 'Travail du métal', color: theme.colors.outchou_blue, image: '/shared_assets/images/tradition/artisanat/travail-metal/kesskess.jpeg' },
    { id: 'poterie', name: 'Poterie', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/artisanat/poterie/kolla.jpeg' },
    { id: 'céramique', name: 'Céramique', color: theme.colors.outchou_blue, image: '/shared_assets/images/tradition/artisanat/ceramique/service.jpeg' },
    { id: 'broderie', name: 'Broderie', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/artisanat/broderie/mejboud.jpeg' },
    { id: 'fibres-tressage', name: 'Fibres et tressage', color: theme.colors.outchou_blue, image: '/shared_assets/images/tradition/artisanat/fibres-tressage/Fibres et tressage.jpg' },
    { id: 'cuir-maroquinerie', name: 'Cuir et maroquinerie', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Cartouchière.png' },
    { id: 'bois', name: 'Bois', color: theme.colors.outchou_blue, image: '/shared_assets/images/tradition/artisanat/bois/Asenduk.jpeg' },
  ];

  // Structure de données pour les sous-éléments de chaque catégorie de vêtements
  const traditionItems: { [key: string]: Array<{ id: string; name: string; image?: string }> } = {
    'costume-feminin': [
      { id: 'robes', name: 'Robes', image: '/shared_assets/images/tradition/vetements/costume-feminin/takendurt.jpeg' },
      { id: 'tenues-completes', name: 'Tenues complètes', image: '/shared_assets/images/tradition/vetements/costume-feminin/lebset-el-arftan.jpg' },
      { id: 'caftans', name: 'Caftans', image: '/shared_assets/images/tradition/vetements/costume-feminin/caftans-cover.jpeg' },
      { id: 'tuniques', name: 'Tuniques', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-kettana.jpeg' },
      { id: 'coiffes-parures-tete', name: 'Coiffes et parures de tête', image: '/shared_assets/images/tradition/vetements/costume-feminin/leffa.jpeg' },
      { id: 'vestes-pieces-dessus', name: 'Vestes et pièces de dessus', image: '/shared_assets/images/tradition/vetements/costume-feminin/burnous.jpeg' },
      { id: 'pantalons', name: 'Pantalons', image: '/shared_assets/images/tradition/vetements/costume-feminin/serouel-mdawar.jpeg' },
    ],
    'costume-masculin': [
      { id: 'robes', name: 'Robes', image: '/shared_assets/images/tradition/vetements/costume-masculin/gandoura.jpeg' },
      { id: 'couvre-chef', name: 'Couvre-chef', image: '/shared_assets/images/tradition/vetements/costume-masculin/takoumbout.jpeg' },
      { id: 'vestes-pieces-dessus', name: 'Vestes et pièces de dessus', image: '/shared_assets/images/tradition/vetements/costume-masculin/karakou.jpeg' },
      { id: 'pantalon', name: 'Pantalon', image: '/shared_assets/images/tradition/vetements/costume-masculin/serouel.jpeg' },
    ],
    'bijoux-accessoires': [
      { id: 'fibule', name: 'Bijoux de tête', image: '/shared_assets/images/tradition/bijoux/fibule/tasabt.jpeg' },
      { id: 'collier', name: 'Collier', image: '/shared_assets/images/tradition/bijoux/collier/skhab-b-johar.jpeg' },
      { id: 'bracelet', name: 'Bracelet', image: '/shared_assets/images/tradition/bijoux/bracelet/msayes.jpeg' },
      { id: 'boucles-oreilles', name: 'Boucles d\'oreilles', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/mengoucha-jouwhara.jpeg' },
      { id: 'bijoux-chevilles', name: 'Bijoux de chevilles', image: '/shared_assets/images/tradition/bijoux/bijoux-chevilles/brim.jpeg' },
      { id: 'autres', name: 'Autres', image: '/shared_assets/images/tradition/bijoux/autres/thihourav.jpeg' },
    ],
    'tenues-disparues': [
      { id: 'tenues-feminines', name: 'Tenues féminines', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/timelheft.jpeg' },
      { id: 'tenues-masculines', name: 'Tenues masculines', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-masculines/kaftan.jpeg' },
      { id: 'bijoux', name: 'Bijoux', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/sarma.jpeg' },
      { id: 'accessoires', name: 'Accessoires', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/cartouchiere.jpeg' },
      { id: 'chaussures', name: 'Chaussures', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/sabbat-el-mejboud.jpeg' },
    ],
  };

  // Sous-sections - Costume féminin
  // NOTE: 2ᵉ niveau uniquement sur les sous-catégories demandées (ex: Robes, Tenues complètes).
  const costumeFemininItems: { [key: string]: Array<{ id: string; name: string; image?: string }> } = {
    robes: [
      { id: 'badriya', name: 'Badriya', image: '/shared_assets/images/tradition/vetements/costume-feminin/badriya.jpeg' },
      { id: 'badroune', name: 'Badroune', image: '/shared_assets/images/tradition/vetements/costume-feminin/badroune.jpeg' },
      { id: 'binouar', name: 'Binouar', image: '/shared_assets/images/tradition/vetements/costume-feminin/binouar.jpeg' },
      { id: 'blouza', name: 'Blouza', image: '/shared_assets/images/tradition/vetements/costume-feminin/blouza.jpeg' },
      { id: 'blouza-mansouj', name: 'Blouza mansouj', image: '/shared_assets/images/tradition/vetements/costume-feminin/blouza-mansouj.jpeg' },
      { id: 'bouza-el-hrir', name: 'Blouza el hrir', image: '/shared_assets/images/tradition/vetements/costume-feminin/blouza-el-hrir.jpeg' },
      { id: 'robba', name: 'Robba', image: '/shared_assets/images/tradition/vetements/costume-feminin/robba.jpeg' },
      { id: 'gandoura', name: 'Gandoura', image: '/shared_assets/images/tradition/vetements/costume-feminin/gandoura.jpeg' },
      { id: 'fergani', name: 'Fergani', image: '/shared_assets/images/tradition/vetements/costume-feminin/fergani.jpeg' },
      { id: 'chamsa', name: 'Chamsa', image: '/shared_assets/images/tradition/vetements/costume-feminin/chamsa.jpeg' },
      { id: 'tawadit', name: 'Tawadit', image: '/shared_assets/images/tradition/vetements/costume-feminin/tawadit.jpeg' },
      { id: 'takendurt', name: 'Takendurt', image: '/shared_assets/images/tradition/vetements/costume-feminin/takendurt.jpeg' },
      { id: 'muwabra', name: 'Muwabra', image: '/shared_assets/images/tradition/vetements/costume-feminin/muwabra.jpeg' },
    ],
    'tenues-completes': [
      { id: 'lebset-el-arftan', name: 'Lebset el arftan', image: '/shared_assets/images/tradition/vetements/costume-feminin/lebset-el-arftan.jpg' },
      { id: 'kwiyet', name: 'Kwiyet', image: '/shared_assets/images/tradition/vetements/costume-feminin/kwiyet.jpeg' },
      { id: 'el-mheyer', name: 'El Mheyer', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-mheyer.jpeg' },
      { id: 'jawali', name: 'Jawali', image: '/shared_assets/images/tradition/vetements/costume-feminin/jawali.jpeg' },
    ],
    caftans: [
      { id: 'mansouria', name: 'Mansouria', image: '/shared_assets/images/tradition/vetements/costume-feminin/mansouria.jpg' },
      { id: 'el-kronfla', name: 'El kronfla', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-kronfla.jpeg' },
      { id: 'el-kadi', name: 'El kadi', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-kadi.jpeg' },
      { id: 'ach-chart', name: 'Ach chart', image: '/shared_assets/images/tradition/vetements/costume-feminin/ach-chart.jpeg' },
      { id: 'el-bahdja', name: 'El bahdja', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-bahdja.jpeg' },
      { id: 'el-fermla', name: 'El fermla', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-fermla.jpg' },
      { id: 'moderne', name: 'Moderne', image: '/shared_assets/images/tradition/vetements/costume-feminin/moderne.jpeg' },
    ],
    tuniques: [
      { id: 'himelheft', name: 'Himelheft', image: '/shared_assets/images/tradition/vetements/costume-feminin/himelheft.jpeg' },
      { id: 'takhlila', name: 'Takhlila', image: '/shared_assets/images/tradition/vetements/costume-feminin/takhlila.jpeg' },
      { id: 'el-rda', name: "El r'da", image: '/shared_assets/images/tradition/vetements/costume-feminin/el-rda.jpeg' },
      { id: 'el-kettana', name: 'El kettana', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-kettana.jpeg' },
      { id: 'el-hawli', name: 'El hawli', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-hawli.jpeg' },
      { id: 'el-houli', name: 'El Houli', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-houli.jpeg' },
      { id: 'izar-gourara', name: 'Izar (Gourara)', image: '/shared_assets/images/tradition/vetements/costume-feminin/izar-gourara.jpeg' },
      { id: 'tasaghnest', name: 'Tasaghnest', image: '/shared_assets/images/tradition/vetements/costume-feminin/tasaghnest.jpeg' },
      { id: 'izar-taghit', name: 'Izar (Taghit)', image: '/shared_assets/images/tradition/vetements/costume-feminin/izar-taghit.jpeg' },
      { id: 'izar-bechar', name: 'Izar (Béchar)', image: '/shared_assets/images/tradition/vetements/costume-feminin/izar-saoura.jpeg' },
      { id: 'izar-adrar', name: 'Izar (Adrar)', image: '/shared_assets/images/tradition/vetements/costume-feminin/izar-adrar.jpeg' },
      { id: 'izar-kerzaz', name: 'Izar (Kerzaz)', image: '/shared_assets/images/tradition/vetements/costume-feminin/izar-kerzaz.jpeg' },
      { id: 'melhfa-biskra', name: 'Melhfa (Biskra)', image: '/shared_assets/images/tradition/vetements/costume-feminin/melhfa-biskra.jpeg' },
    ],
    pantalons: [
      { id: 'serouel-chelka', name: 'Serouel chelka', image: '/shared_assets/images/tradition/vetements/costume-feminin/serouel-chelka.jpeg' },
      { id: 'serouel-mdawar', name: 'Serouel mdawar', image: '/shared_assets/images/tradition/vetements/costume-feminin/serouel-mdawar.jpeg' },
      { id: 'serouel-testifa', name: 'Serouel testifa', image: '/shared_assets/images/tradition/vetements/costume-feminin/serouel-testifa.jpeg' },
    ],
    'vestes-pieces-dessus': [
      { id: 'karakou-veste', name: 'Karakou', image: '/shared_assets/images/tradition/vetements/costume-feminin/karakou.jpeg' },
      { id: 'kat', name: 'Kat', image: '/shared_assets/images/tradition/vetements/costume-feminin/kat.jpeg' },
      { id: 'jellaba', name: 'Jellaba', image: '/shared_assets/images/tradition/vetements/costume-feminin/jellaba.jpeg' },
      { id: 'burnous', name: 'Burnous', image: '/shared_assets/images/tradition/vetements/costume-feminin/burnous.jpeg' },
      { id: 'el-ghaness', name: 'El ghaness', image: '/shared_assets/images/tradition/vetements/costume-feminin/el-ghaness.jpeg' },
    ],
    'coiffes-parures-tete': [
      { id: 'chedda', name: 'Chedda', image: '/shared_assets/images/tradition/vetements/costume-feminin/chedda.jpeg' },
      { id: 'chedda-el-jess', name: 'Chedda el jess', image: '/shared_assets/images/tradition/vetements/costume-feminin/chedda-jess.jpeg' },
      { id: 'tegrifa', name: 'Tegrifa', image: '/shared_assets/images/tradition/vetements/costume-feminin/tegrifa.jpeg' },
      { id: 'tegrifa-fertas', name: 'Tegrifa fertas', image: '/shared_assets/images/tradition/vetements/costume-feminin/tegrifa-fertas.jpeg' },
      { id: 'dlella', name: 'Dlella', image: '/shared_assets/images/tradition/vetements/costume-feminin/dlella.jpeg' },
      { id: 'leffa', name: 'Leffa', image: '/shared_assets/images/tradition/vetements/costume-feminin/leffa.jpeg' },
    ],
  };

  // Sous-sections - Costume masculin
  const costumeMasculinItems: { [key: string]: Array<{ id: string; name: string; image?: string }> } = {
    robes: [{ id: 'gandoura', name: 'Gandoura', image: '/shared_assets/images/tradition/vetements/costume-masculin/gandoura.jpeg' }],
    'couvre-chef': [
      { id: 'mdal', name: 'Mdal', image: '/shared_assets/images/tradition/vetements/costume-masculin/mdal.jpeg' },
      { id: 'chachiya', name: 'Chachiya', image: '/shared_assets/images/tradition/vetements/costume-masculin/chachiya.jpeg' },
      { id: 'takoumbout', name: 'Takoumbout', image: '/shared_assets/images/tradition/vetements/costume-masculin/takoumbout.jpeg' },
      { id: 'aamama', name: 'Aamama', image: '/shared_assets/images/tradition/vetements/costume-masculin/aamama.jpeg' },
    ],
    'vestes-pieces-dessus': [
      { id: 'jellaba', name: 'Jellaba', image: '/shared_assets/images/tradition/vetements/costume-masculin/jellaba.jpeg' },
      { id: 'kachabia', name: 'Kachabia', image: '/shared_assets/images/tradition/vetements/costume-masculin/kachabia.jpeg' },
      { id: 'karakou', name: 'Karakou', image: '/shared_assets/images/tradition/vetements/costume-masculin/karakou.jpeg' },
      { id: 'barnous', name: 'Barnous', image: '/shared_assets/images/tradition/vetements/costume-masculin/barnous.jpeg' },
    ],
    pantalon: [
      { id: 'serouel-testifa', name: 'Serouel testifa', image: '/shared_assets/images/tradition/vetements/costume-masculin/serouel.jpeg' },
      { id: 'serouel-mdawar', name: 'Serouel mdawar', image: '/shared_assets/images/tradition/vetements/costume-masculin/serouel-mdawar.jpeg' },
    ],
  };

  // Sous-sections - Bijoux & accessoires
  const bijouxItems: { [key: string]: Array<{ id: string; name: string; image?: string }> } = {
    fibule: [
      { id: 'chachiya', name: 'Chachiya', image: '/shared_assets/images/tradition/bijoux/fibule/chachiya.jpeg' },
      { id: 'el-garn', name: 'El garn', image: '/shared_assets/images/tradition/bijoux/fibule/el-garn.jpeg' },
      { id: 'chachiya-b-soltani', name: 'Chachiya b soltani', image: '/shared_assets/images/tradition/bijoux/fibule/chachiya-b-soltani.jpeg' },
      { id: 'khoss', name: 'Khoss', image: '/shared_assets/images/tradition/bijoux/fibule/khoss.jpeg' },
      { id: 'jbine', name: 'Jbine', image: '/shared_assets/images/tradition/bijoux/fibule/jbine.jpeg' },
      { id: 'zerouf', name: 'Zerouf', image: '/shared_assets/images/tradition/bijoux/fibule/zerouf.jpeg' },
      { id: 'khait-el-hout', name: 'Khaït el hout', image: '/shared_assets/images/tradition/bijoux/fibule/khait-el-hout.jpeg' },
      { id: 'khait-ech-chair', name: 'Khaït ech chaïr', image: '/shared_assets/images/tradition/bijoux/fibule/khait-ech-chair.jpeg' },
      { id: 'tej', name: 'Tej', image: '/shared_assets/images/tradition/bijoux/fibule/tej.jpeg' },
      { id: 'tasabt', name: 'Ta\'sabt', image: '/shared_assets/images/tradition/bijoux/fibule/tasabt.jpeg' },
      { id: 'warda-kronfla', name: 'Warda/Kronfla', image: '/shared_assets/images/tradition/bijoux/fibule/warda-kronfla.jpeg' },
    ],
    collier: [
      { id: 'skhab-b-johar', name: 'Skhab b johar', image: '/shared_assets/images/tradition/bijoux/collier/skhab-b-johar.jpeg' },
      { id: 'skhab-b-anbar', name: 'Skhab b anbar', image: '/shared_assets/images/tradition/bijoux/collier/skhab-b-anbar.jpeg' },
      { id: 'skhab-b-dahab', name: 'Skhab b dahab', image: '/shared_assets/images/tradition/bijoux/collier/skhab-b-dahab.jpeg' },
      { id: 'sensla', name: 'Sensla', image: '/shared_assets/images/tradition/bijoux/collier/sensla.jpeg' },
      { id: 'azrar', name: 'Azrar', image: '/shared_assets/images/tradition/bijoux/collier/azrar.jpeg' },
      { id: 'cravacha-boulahiya', name: 'Cravacha boulahiya', image: '/shared_assets/images/tradition/bijoux/collier/cravacha-boulahiya.jpeg' },
      { id: 'thazath', name: 'Thazath', image: '/shared_assets/images/tradition/bijoux/collier/thazath.jpeg' },
      { id: 'mkhabel', name: 'Mkhabel', image: '/shared_assets/images/tradition/bijoux/collier/mkhabel.jpeg' },
      { id: 'bassit', name: 'Bassit', image: '/shared_assets/images/tradition/bijoux/collier/bassit.jpeg' },
      { id: 'johar', name: 'Johar', image: '/shared_assets/images/tradition/bijoux/collier/johar.jpeg' },
      { id: 'meskia', name: 'Meskia', image: '/shared_assets/images/tradition/bijoux/collier/meskia.jpeg' },
      { id: 'Guendzi', name: 'Guendzi', image: '/shared_assets/images/tradition/bijoux/collier/Guendzi.jpeg' }
    ],
    'boucles-oreilles': [
      { id: 'nouacha', name: 'Nouacha', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/nouacha.jpeg' },
      { id: 'chouchnet', name: 'Chouchnet', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/chouchnet.jpeg' },
      { id: 'thigoudmatin', name: 'Thigoudmatin', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/thigoudmatin.jpeg' },
      { id: 'tizabaten', name: 'Tizabaten', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/tizabaten.jpeg' },
      { id: 'mengoucha-jouwhara', name: 'Mengoucha jouwhara', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/mengoucha-jouwhara.jpeg' },
      { id: 'meneguich-njoud', name: 'Meneguich njoud', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/meneguich-njoud.jpeg' },
      { id: 'khorsa', name: 'Khorsa', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/khorsa.jpeg' },
      { id: 'teraout', name: 'Teraout', image: '/shared_assets/images/tradition/bijoux/boucles-oreilles/teraout.jpeg' }
    ],
    bracelet: [
      { id: 'dah', name: 'Dah', image: '/shared_assets/images/tradition/bijoux/bracelet/dah.jpeg' },
      { id: 'lmegzoum', name: 'Lmegzoum', image: '/shared_assets/images/tradition/bijoux/bracelet/lmegzoum.jpeg' },
      { id: 'amesluh', name: 'Amesluh', image: '/shared_assets/images/tradition/bijoux/bracelet/amesluh.jpeg' },
      { id: 'amechloukh', name: 'Amechloukh', image: '/shared_assets/images/tradition/bijoux/bracelet/amechloukh.jpeg' },
      { id: 'maqias-b-louise', name: 'Maqias b louise', image: '/shared_assets/images/tradition/bijoux/bracelet/maqias-b-louise.jpeg' },
      { id: 'msayes', name: 'Msayes', image: '/shared_assets/images/tradition/bijoux/bracelet/msayes.jpeg' },
      { id: 'Ahbeg', name: 'Ahbeg', image: '/shared_assets/images/tradition/bijoux/bracelet/Ahbeg.jpeg' },
      { id: 'ihebgen', name: 'Ihebgen', image: '/shared_assets/images/tradition/bijoux/bracelet/ihebgen.jpeg' },
      { id: 'amekias', name: 'Amekias', image: '/shared_assets/images/tradition/bijoux/bracelet/amekias.jpeg' },
    ],
    'bijoux-chevilles': [
      { id: 'brim', name: 'Brim', image: '/shared_assets/images/tradition/bijoux/bijoux-chevilles/brim.jpeg' },
      { id: 'ikhelkhalen', name: 'Ikhelkhalen', image: '/shared_assets/images/tradition/bijoux/bijoux-chevilles/ikhelkhalen.jpeg' },
      { id: 'el kiss', name: 'El kiss', image: '/shared_assets/images/tradition/bijoux/bijoux-chevilles/el-kiss.jpeg' }
    ],
    autres: [
      { id: 'khatem', name: 'Khatem', image: '/shared_assets/images/tradition/bijoux/autres/khatem.jpeg' },
      { id: 'takhatemt', name: 'Takhatemt', image: '/shared_assets/images/tradition/bijoux/autres/takhatemt.jpeg' },
      { id: 'thihourav', name: 'Thihourav', image: '/shared_assets/images/tradition/bijoux/autres/thihourav.jpeg' },
      { id: 'hazama', name: 'hazama', image: '/shared_assets/images/tradition/bijoux/autres/hazama.jpeg' },
    ],
  };

  // Sous-sections - Éléments disparus
  const tenuesDisparuesItems: { [key: string]: Array<{ id: string; name: string; image?: string }> } = {
    'tenues-feminines': [
      { id: 'ghlila', name: 'Ghlila', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/ghlila.jpeg' },
      { id: 'firmla', name: 'Firmla', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/firmla.jpeg' },
      { id: 'melhfa-algeroise', name: 'Melhfa algéroise', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/melhfa-algeroise.jpeg' },
      { id: 'ghlila-djabadouli', name: 'Ghlila djabadouli', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/ghlila-djabadouli.jpeg' },
      { id: 'mlaya', name: 'Mlaya', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/mlaya.jpeg' },
      { id: 'timelheft', name: 'Timelheft', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/timelheft.jpeg' },
      
      { id: 'gnidra', name: 'Gnidra', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/gnidra.webp' },
      { id: 'djebba', name: 'Djebba', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/djebba.jpeg' },
      { id: 'keswa-el-kabira', name: 'Keswa el kabira', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-feminines/keswa-el-kabira.jpeg' },
    ],
    'tenues-masculines': [
      { id: 'kaftan', name: 'Kaftan', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-masculines/kaftan.jpeg' },
      { id: 'jabador', name: 'Jabador', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-masculines/jabador.webp' },
      { id: 'bedriya', name: 'Bedriya', image: '/shared_assets/images/tradition/vetements/elements-disparus/tenues-masculines/bedriya.jpeg' },
    ],
    bijoux: [
      { id: 'chachiya-tassa', name: 'Chachiya tassa', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/chachiya-tassa.jpeg' },
      { id: 'chachiya-mekellela', name: 'Chachiya mekellela', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/chachiya-mekellela.jpeg' },
      { id: 'sarma', name: 'Sarma', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/sarma.jpeg' },
      { id: 'zerir', name: 'Zerir', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/zerir.jpeg' },
      { id: 'acabech', name: 'Acabech', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/acabech.jpeg' },
      { id: 'anadj', name: 'Anadj', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/anadj.jpeg' },
      { id: 'tselset-chaoui', name: 'Tselset (chaoui)', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tselset-chaoui.jpg' },
      { id: 'tselset-kabyle', name: 'Tselset (kabyle)', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tselset-kabyle.jpeg' },
      { id: 'abzim', name: 'Abzim', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/abzim.jpeg' },
      { id: 'latrak', name: 'Latrak', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/latrak.jpg' },
      { id: 'thialloukin', name: 'Thialloukin', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/thialloukin.jpg' },
      { id: 'taounist', name: 'Taounist', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/taounist.jpeg' },
      { id: 'kounais', name: 'Kounaïs', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/kounais.jpeg' },
      { id: 'hanaeast', name: 'Hanaeast', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/hanaeast.jpeg' },
      { id: 'tanit', name: 'Tanit', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tanit.jpeg' },
      { id: 'hichenchounine', name: 'Hichenchounine', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/hichenchounine.jpeg' },
      { id: 'haalaqth', name: 'Haalaqth', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/haalaqth.jpeg' },
      { id: 'alaqa-b-chebak', name: 'Alaqa b chebak', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/alaqa-b-chebak.jpeg' },
      { id: 'himcherreft', name: 'Himcherreft', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/himcherreft.jpeg' },
      { id: 'mcharek', name: 'Mcharek', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/mcharek.jpeg' },
      { id: 'hcherket', name: 'Hcherket', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/hcherket.jpeg' },
      { id: 'aakad', name: 'Aakad', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/aakad.jpeg' },
      { id: 'tazlegt-mlherz', name: 'Tazlegt m\'lherz', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tazlegt-mlherz.jpeg' },
      { id: 'qelada', name: 'Qelada', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/qelada.jpeg' },
      { id: 'medibah', name: 'Medibah', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/medibah.jpeg' },
      { id: 'derga-selsela', name: 'Derga selsela', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/derga-selsela.webp' },
      { id: 'tcherot', name: 'Tcherot', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tcherot.jpeg' },
      { id: 'aquaran', name: 'Aquaran', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/aquaran.jpeg' },
      { id: 'korkoro', name: 'Korkoro', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/korkoro.jpeg' },
      { id: 'berzouan', name: 'Berzouan', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/berzouan.jpeg' },
      { id: 'khomaissa', name: 'Khomaïssa', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/khomaissa.jpeg' },
      { id: 'harz', name: 'Harz', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/harz.jpeg' },
      { id: 'tehlilat', name: 'Tehlilat', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tehlilat.jpeg' },
      { id: 'kitab', name: 'Kitab', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/kitab.jpg' },
      { id: 'nedjma', name: 'Nedjma', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/nedjma.jpeg' },
      { id: 'tanaghilt', name: 'Tanaghilt', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tanaghilt.jpeg' },
      { id: 'tabzimt', name: 'Tabzimt', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tabzimt.jpeg' },
      { id: 'mdawar', name: 'Mdawar' },
      { id: 'habzimt', name: 'Habzimt', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/habzimt.jpeg' },
      { id: 'chamassa', name: 'Chamassa', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/chamassa.jpeg' },
      { id: 'taharabt', name: 'Taharabt', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/taharabt.jpeg' },
      { id: 'khelala-constantine', name: 'Khelala (Constantine)', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/khelala-constantine.jpeg' },
      { id: 'ibzimen', name: 'Ibzimen', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/ibzimen.jpeg' },
      { id: 'tibzimin', name: 'Tibzimin', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/tibzimin.jpeg' },
      { id: 'souar-chaoui', name: 'Souar (Chaoui)', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/souar-chaoui.jpeg' },
      { id: 'mechbek', name: 'Mechbek', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/mechbek.jpeg' },
      { id: 'menfoukh', name: 'Menfoukh', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/menfoukh.jpeg' },
      { id: 'dah-deg-el-hommos', name: 'Dah deg el hommos', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/dah-deg-el-hommos.jpeg' },
      { id: 'dbalidj', name: 'Dbalidj', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/dbalidj.jpg' },
      { id: 'souar', name: 'Souar', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/souar.jpeg' },
      { id: 'rdaif', name: 'Rdaïf', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/rdaif.jpeg' },
      { id: 'ardif-kabylie', name: 'Ardif (Kabylie)', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/ardif-kabylie.jpeg' },
      { id: 'ardif-chaoui', name: 'Ardif (Chaoui)', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/ardif-chaoui.jpeg' },
      { id: 'nouach', name: 'Nouach', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/nouach.jpeg' },
      { id: 'amsak-chaoui', name: 'Amsak (Chaoui)', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/amsak-chaoui.jpeg' },
      { id: 'lahiyet-el-kadi', name: 'Lahiyet el Kadi', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/lahiyet-el-kadi.jpg' },
      { id: 'amechhoud', name: 'Amechhoud', image: '/shared_assets/images/tradition/vetements/elements-disparus/bijoux/amechhoud.jpeg' },
    ],
    accessoires: [
      { id: 'meriouha', name: 'Meriouha', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/meriouha.jpeg' },
      { id: 'hisigrest', name: 'Hisigrest', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/hisigrest.jpg' },
      { id: 'tasegerst', name: 'Tasegerst', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/tasegerst.jpeg' },
      { id: 'hisirist', name: 'Hisirist', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/hisirist.jpg' },
      { id: 'el-jebira', name: 'El jebira', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/el-jebira.jpeg' },
      { id: 'cartouchiere', name: 'Cartouchière', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/cartouchiere.jpeg' },
      { id: 'tesdam', name: 'Tesdam', image: '/shared_assets/images/tradition/vetements/elements-disparus/accessoires/tesdam.jpeg' },
    ],
    chaussures: [
      { id: 'blegha', name: 'Blegha', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/blegha.jpeg' },
      { id: 'khouff', name: 'Khouff', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/khouff.jpeg' },
      { id: 'rihait', name: 'Rihaïet', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/rihait.jpeg' },
      { id: 'babouche', name: 'Babouche', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/babouche.jpeg' },
      { id: 'sabbat-el-mejboud', name: 'Sabbat el mejboud', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/sabbat-el-mejboud.jpeg' },
      { id: 'kabkab', name: 'Kabkab', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/kabkab.jpeg' },
      { id: 'sabat', name: 'Sabat', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/sabat.jpeg' },
      { id: 'Affane', name: 'Affane', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/affane.jpg' },
      { id: 'Toumobilète', name: 'Toumobilète', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/toumobilete.jpeg' },
    ],
  };

  // Structure de données pour les sous-éléments de chaque catégorie d'artisanat
  const artisanatItems: { [key: string]: Array<{ id: string; name: string; image?: string }> } = {
    'tapisserie': [
      { id: 'dokkali (touat)', name: 'Dokkali (Touat)', image: '/shared_assets/images/tradition/artisanat/tapisserie/dokkali-touat.jpeg' },
      { id: 'dokkali (gourara)', name: 'Dokkali (Gourara)', image: '/shared_assets/images/tradition/artisanat/tapisserie/dokkali-gourara.jpg' },
      { id: 'dokkali (tidikelt)', name: 'Dokkali (Tidikelt)', image: '/shared_assets/images/tradition/artisanat/tapisserie/dokkali-tidikelt.jpeg' },
      { id: 'maadid', name: 'Maadid', image: '/shared_assets/images/tradition/artisanat/tapisserie/maadid.jpeg' },
      { id: 'ragma', name: 'Ragma', image: '/shared_assets/images/tradition/artisanat/tapisserie/ragma.jpeg' },
      { id: 'nememcha', name: 'Nememcha', image: '/shared_assets/images/tradition/artisanat/tapisserie/nememcha.jpeg' },
      { id: 'guergour', name: 'Guergour', image: '/shared_assets/images/tradition/artisanat/tapisserie/guergour.jpeg' },
      { id: 'bou taleb', name: 'Bou Taleb', image: '/shared_assets/images/tradition/artisanat/tapisserie/bou-taleb.jpeg' },
      { id: 'Djerbi', name: 'Djerbi', image: '/shared_assets/images/tradition/artisanat/tapisserie/djerbi.jpeg' },
      { id: 'ddil', name: 'Ddil', image: '/shared_assets/images/tradition/artisanat/tapisserie/ddil.jpeg' },
      { id: 'Tellis', name: 'Tellis', image: '/shared_assets/images/tradition/artisanat/tapisserie/tellis.jpeg' },
      { id: 'Zerbiya relizane', name: 'Zerbiya Relizane', image: '/shared_assets/images/tradition/artisanat/tapisserie/zerbiya-relizane.jpeg' },
      { id: 'Zerbiya chaouïa', name: 'Zerbiya Chaouïa', image: '/shared_assets/images/tradition/artisanat/tapisserie/zerbiya-chaouia.jpeg' },
      { id: 'Jbel amor', name: 'Jbel Amor', image: '/shared_assets/images/tradition/artisanat/tapisserie/jbel-amor.jpg' },
      { id: 'Soumam', name: 'Soumam', image: '/shared_assets/images/tradition/artisanat/tapisserie/soumam.jpeg' },
      { id: 'El oued', name: 'El Oued', image: '/shared_assets/images/tradition/artisanat/tapisserie/el-oued.jpeg' },
      { id: 'Alger', name: 'Alger', image: '/shared_assets/images/tradition/artisanat/tapisserie/alger.jpeg' },
    ],
    'broderie': [
      { id: 'fetla', name: 'Fetla', image: '/shared_assets/images/tradition/artisanat/broderie/fetla.jpeg' },
      { id: 'mejboud', name: 'Mejboud', image: '/shared_assets/images/tradition/artisanat/broderie/mejboud.jpeg' },
      { id: 'chaaira', name: 'Chaaira' },
    ],
    'poterie': [
      { id: 'methred', name: 'Methred', image: '/shared_assets/images/tradition/artisanat/poterie/methred.jpeg' },
      { id: 'kolla', name: 'Kolla', image: '/shared_assets/images/tradition/artisanat/poterie/kolla.jpeg' },
      { id: 'gasra', name: 'Gasra', image: '/shared_assets/images/tradition/artisanat/poterie/gasra.jpeg' },
    ],
    'céramique': [
      { id: 'tobsi', name: 'Tobsi', image: '/shared_assets/images/tradition/artisanat/ceramique/tobsi.jpeg' },
      { id: 'kolla', name: 'Kolla', image: '/shared_assets/images/tradition/artisanat/ceramique/kolla.jpeg' },
      { id: 'service', name: 'Service', image: '/shared_assets/images/tradition/artisanat/ceramique/service.jpeg' },
    ],
    'travail-metal': [
      { id: 'mkahla', name: 'Mkahla', image: '/shared_assets/images/tradition/artisanat/travail-metal/mkahla.jpeg' },
      { id: 'meroued', name: 'Meroued', image: '/shared_assets/images/tradition/artisanat/travail-metal/meroued.jpeg' },  
      { id: 'mbkhara', name: 'Mbkhara', image: '/shared_assets/images/tradition/artisanat/travail-metal/mbkhara.jpeg' },
      { id: 'boqraj', name: 'Boqraj', image: '/shared_assets/images/tradition/artisanat/travail-metal/boqraj.jpeg' },
      { id: 'siniya', name: 'Siniya', image: '/shared_assets/images/tradition/artisanat/travail-metal/siniya.jpeg' },
      { id: 'keskess', name: 'Kesskess', image: '/shared_assets/images/tradition/artisanat/travail-metal/kesskess.jpeg' },
      { id: 'babour', name: 'Babour', image: '/shared_assets/images/tradition/artisanat/travail-metal/babour.webp' },
      { id: 'zarf', name: 'Zarf', image: '/shared_assets/images/tradition/artisanat/travail-metal/zarf.jpeg' },
      { id: 'rbia', name: 'Rbia', image: '/shared_assets/images/tradition/artisanat/travail-metal/rbia.jpeg' },
      { id: 'berrad', name: 'Berrad', image: '/shared_assets/images/tradition/artisanat/travail-metal/berrad.jpeg' },
      { id: 'heurtoir de porte', name: 'Heurtoir de porte', image: '/shared_assets/images/tradition/artisanat/travail-metal/heurtoir-de-porte.jpeg' },
      { id: 'El yenn', name: 'El yenn', image: '/shared_assets/images/tradition/artisanat/travail-metal/el-yenn.jpeg' },
      { id: 'mracha', name: 'Mracha', image: '/shared_assets/images/tradition/artisanat/travail-metal/mracha.jpeg' },
      { id: 'mehraz', name: 'Mehraz', image: '/shared_assets/images/tradition/artisanat/travail-metal/mehraz.jpeg' },
      { id: 'nimcha', name: 'Nimcha', image: '/shared_assets/images/tradition/artisanat/travail-metal/nimcha.jpeg' },
      { id: 'flyssa', name: 'Flyssa', image: '/shared_assets/images/tradition/artisanat/travail-metal/flyssa.jpeg' },
      { id: 'mokahla', name: 'Mokahla', image: '/shared_assets/images/tradition/artisanat/travail-metal/mokahla.jpeg' },
      { id: 'kabous', name: 'Kabous', image: '/shared_assets/images/tradition/artisanat/travail-metal/kabous.jpeg' },
      { id: 'sif', name: 'Sif', image: '/shared_assets/images/tradition/artisanat/travail-metal/sif.jpeg' },
      { id: 'mahbés', name: 'Mahbés', image: '/shared_assets/images/tradition/artisanat/travail-metal/mahbes.jpeg' },
      { id: 'Setla', name: 'Setla', image: '/shared_assets/images/tradition/artisanat/travail-metal/setla.jpeg' },
      { id: 'Tassa', name: 'Tassa', image: '/shared_assets/images/tradition/artisanat/travail-metal/tassa.jpeg' },
      { id: 'tanghenjayt', name: 'Tanɣenjayt', image: '/shared_assets/images/tradition/artisanat/travail-metal/tanghenjayt.png' },
    ],
    'fibres-tressage': [
      { id: 'taddara', name: 'Taddara', image: '/shared_assets/images/tradition/artisanat/fibres-tressage/Taddara.jpg' },
      { id: 'tamames-n-taddara', name: 'Tamames n taddara', image: '/shared_assets/images/tradition/artisanat/fibres-tressage/Tamames n taddara.jpeg' },
      { id: 'tbag', name: 'Tbag', image: '/shared_assets/images/tradition/artisanat/fibres-tressage/Tbag.png' },
      { id: 'fendi-kedda', name: 'Fendi kedda', image: '/shared_assets/images/tradition/artisanat/fibres-tressage/Fendi kedda.jpeg' },
      { id: 'gouffa', name: 'Gouffa', image: '/shared_assets/images/tradition/artisanat/fibres-tressage/Gouffa.png' },
      { id: 'mghata', name: 'Mghata', image: '/shared_assets/images/tradition/artisanat/fibres-tressage/Mghata.jpg' },
    ],
    'cuir-maroquinerie': [
      { id: 'babouche', name: 'Babouche', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/babouche.jpeg' },
      { id: 'blegha', name: 'Blegha', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Blegha.jpeg' },
      { id: 'bottes', name: 'Bottes', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Bottes.jpeg' },
      { id: 'bouctou', name: 'Bouctou', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Bouctou.jpeg' },
      { id: 'bride-de-cheval', name: 'Bride de cheval', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Bride de cheval.png' },
      { id: 'cartouchiere', name: 'Cartouchière', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Cartouchière.png' },
      { id: 'el-jebira', name: 'El jebira', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/El jebira.jpeg' },
      { id: 'hazama', name: 'Hazama', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Hazama.png' },
      { id: 'ilebi', name: 'Ilebi', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Illebi.png' },
      { id: 'khouff', name: 'Khouff', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Khouff.jpeg' },
      { id: 'poire-a-poudre', name: 'Poire à poudre', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Poire à poudre.png' },
      { id: 'poitrail-de-cheval', name: 'Poitrail de cheval', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Poitrail de cheval.png' },
      { id: 'rihaiet', name: 'Rihaïet', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/rihait.jpeg' },
      { id: 'sabat', name: 'Sabat', image: '/shared_assets/images/tradition/vetements/elements-disparus/chaussures/sabat.jpeg' },
      { id: 'saccoche', name: 'Saccoche', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Saccoche.jpeg' },
      { id: 'selle-de-cheval', name: 'Selle de cheval', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/selle de cheval.jpeg' },
      { id: 'sguiba', name: 'Sguiba', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Sguiba.jpeg' },
      { id: 'tatttap', name: 'Tattap', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Tattap.png' },
      { id: 'telaq', name: 'Telaq', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Telaq.png' },
      { id: 'tesdam', name: 'Tesdam', image: '/shared_assets/images/tradition/artisanat/cuir-maroquinerie/Tesdam.jpeg' },
    ],
    'bois': [
      { id: 'sandouk', name: 'Sandouk', image: '/shared_assets/images/tradition/artisanat/bois/Sandouk.jpeg' },
      { id: 'bab', name: 'Bab', image: '/shared_assets/images/tradition/artisanat/bois/Bab.jpeg' },
      { id: 'bata', name: 'Bata', image: '/shared_assets/images/tradition/artisanat/bois/Bata.jpeg' },
      { id: 'gaine', name: 'Gaine', image: '/shared_assets/images/tradition/artisanat/bois/Gaine.png' },
      { id: 'gassra', name: 'Gassra', image: '/shared_assets/images/tradition/artisanat/bois/Gassra.png' },
      { id: 'kabkab', name: 'Kabkab', image: '/shared_assets/images/tradition/artisanat/bois/Kabkab.png' },
      { id: 'mechred', name: 'Mechred', image: '/shared_assets/images/tradition/artisanat/bois/Mechred.png' },
      { id: 'meida', name: 'Meïda', image: '/shared_assets/images/tradition/artisanat/bois/Meïda.jpeg' },
      { id: 'moghrof', name: 'Moghrof', image: '/shared_assets/images/tradition/artisanat/bois/Moghrof.png' },
      { id: 'moucharabieh', name: 'Moucharabieh', image: '/shared_assets/images/tradition/artisanat/bois/Moucharabieh.jpeg' },
      { id: 'porte-coran', name: 'Porte coran', image: '/shared_assets/images/tradition/artisanat/bois/Porte coran.png' },
      { id: 'tabaa', name: 'Tabaa', image: '/shared_assets/images/tradition/artisanat/bois/Taba3.png' },
    ],
  };

  const musiqueCategories = [
    { id: 'instruments-traditionnels', name: 'Instruments traditionnels', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/instruments/rebab.jpg' },
    { id: 'danses', name: 'Danses', color: theme.colors.outchou_blue },
    { id: 'musiques-chants', name: 'Musiques et chants', color: theme.colors.outchou_green },
  ];

  // Sous-catégories pour Instruments traditionnels
  const instrumentsSubcategories = [
    { id: 'instruments-cordes', name: 'Instruments à cordes', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/instruments/mandole-algerienne.jpg' },
    { id: 'instruments-vent', name: 'Instruments à vent', color: theme.colors.outchou_blue, image: '/shared_assets/images/tradition/instruments/gasba.jpg' },
    { id: 'instruments-percussion', name: 'Instruments à percussion', color: theme.colors.outchou_green, image: '/shared_assets/images/tradition/instruments/bendir.jpg' },
  ];

  // Instruments avec descriptions et images pour chaque sous-catégorie
  const instrumentsItems: { [key: string]: Array<{ id: string; name: string; description: string; image?: string }> } = {
    'instruments-cordes': [
      { 
        id: 'oud', 
        name: 'Oud', 
        description: 'L\'oud est un luth arabe majeur, dont l\'histoire remonte au VIIᵉ siècle dans la région de Bagdad. Il a été introduit en Espagne par les nord-africains au IXᵉ siècle et est à l\'origine du luth européen. En Algérie, il est utilisé dans la musique arabo-andalouse et le chaâbi.',
        image: '/shared_assets/images/tradition/instruments/oud.jpg'
      },
      { 
        id: 'rebab', 
        name: 'Rebab', 
        description: 'Instrument à archet d\'origine arabe, le rebab est utilisé dans la musique citadine, notamment à Tlemcen, où il s\'inscrit dans la tradition arabo-andalouse.',
        image: '/shared_assets/images/tradition/instruments/rebab.jpg'
      },
      { 
        id: 'mandole-algerienne', 
        name: 'Mandole algérienne', 
        description: 'Créée en 1930, la mandole est une variante de la mandoline, devenue essentielle dans la musique chaâbi.',
        image: '/shared_assets/images/tradition/instruments/mandole-algerienne.jpg'
      },
      { 
        id: 'guembri', 
        name: 'Guembri (ou gambri)', 
        description: 'Petite mandoline traditionnelle utilisée notamment dans certaines musiques de l\'Algérie saharienne.',
        image: '/shared_assets/images/tradition/instruments/guembri.jpg'
      },
      { 
        id: 'imzad', 
        name: 'Imzad', 
        description: 'Violon monocorde joué principalement par les femmes touarègues à Tamanrasset. L\'imzad est inscrit au patrimoine culturel immatériel de l\'UNESCO.',
        image: '/shared_assets/images/tradition/instruments/imzad.jpg'
      },
    ],
    'instruments-vent': [
      { 
        id: 'gasba', 
        name: 'Gasba', 
        description: 'Flûte oblique en roseau, utilisée dans la musique chaouie, le raï traditionnel et le bedoui oranais. Son usage remonte à l\'Antiquité.',
        image: '/shared_assets/images/tradition/instruments/gasba.jpg'
      },
      { 
        id: 'ney', 
        name: 'Ney', 
        description: 'Flûte droite présente dans la musique arabe classique.',
        image: '/shared_assets/images/tradition/instruments/ney.jpg'
      },
      { 
        id: 'ghaita', 
        name: 'Ghaïta (ou zurna)', 
        description: 'Hautbois traditionnel, utilisé dans divers styles populaires et cérémoniels.',
        image: '/shared_assets/images/tradition/instruments/ghaita.jpg'
      },
    ],
    'instruments-percussion': [
      { 
        id: 'derbouka', 
        name: 'Derbouka', 
        description: 'Tambour en forme de gobelet, d\'origine moyen-orientale, largement utilisé dans la musique arabe et algérienne.',
        image: '/shared_assets/images/tradition/instruments/derbouka.jpg'
      },
      { 
        id: 'bendir', 
        name: 'Bendir', 
        description: 'Tambour sur cadre répandu dans la musique amazigh et arabe du Maghreb.',
        image: '/shared_assets/images/tradition/instruments/bendir.jpg'
      },
      { 
        id: 'kadous-gallal', 
        name: 'Kadous / Gallal', 
        description: 'Grand tambourin cylindrique en terre cuite ou en métal, utilisé dans la musique bédouine et populaire algérienne.',
        image: '/shared_assets/images/tradition/instruments/kadous-gallal.jpg'
      },
      { 
        id: 'karkabou', 
        name: 'Karkabou', 
        description: 'Castagnettes métalliques associées à la musique gnawa et aux traditions diwane.',
        image: '/shared_assets/images/tradition/instruments/karkabou.jpg'
      },
      { 
        id: 'tbel', 
        name: 'Tbel (ou tbal)', 
        description: 'Grand tambour utilisé dans la musique rurale algérienne.',
        image: '/shared_assets/images/tradition/instruments/tbel.jpg'
      },
    ],
  };

  // Structure de données pour les sous-éléments de chaque catégorie de musique
  const musiqueItems: { [key: string]: Array<{ id: string; name: string }> } = {
    'danses': [
      { id: 'aroubi', name: 'Aroubi' },
      { id: 'hadra', name: 'Hadra' },
      { id: 'rachidia', name: 'Rachidia' },
      { id: 'raï', name: 'Raï' },
      { id: 'rahaba', name: 'Rahaba' },
      { id: 'El houzi', name: 'El houzi' },
      { id: 'El goum', name: 'El goum' },
      { id: 'El jedib', name: 'El jedib' },
      { id: 'Danse de la jument', name: 'Danse de la jument' },
      { id: 'Danse d Oued Righ', name: 'Danse d oued righ' },
      { id: 'Danse touareg féminine', name: 'Danse touareg féminine' },
      { id: 'Danse touareg masculine', name: 'Danse touareg masculine' },
      { id: 'Danse kabyle féminine', name: 'Danse kabyle féminine' },
      { id: 'Danse kabyle masculine', name: 'Danse kabyle masculine' },
      ],
    'musiques-chants': [
      { id: 'andalouses', name: 'Andalouses' },
      { id: 'chants-feminin', name: 'Chants féminin' },
      { id: 'musiques-populaires-vivantes', name: 'Musiques populaires et vivantes' },
      { id: 'musiques-regionales', name: 'Musiques régionales' },
    ],
  };

  // Sous-éléments pour Musiques et chants
  const musiquesChantSubItems: { [key: string]: Array<{ id: string; name: string }> } = {
    'andalouses': [
      { id: 'gharnati', name: 'Gharnati' },
      { id: 'maalouf', name: 'Maalouf' },
      { id: 'hawzi', name: 'Hawzi' },
      { id: 'hawfi', name: 'Hawfi' },
      { id: 'caana', name: "Ca'ana" },
      { id: 'zjoul', name: 'Zjoul' },
    ],
    'chants-feminin': [
      { id: 'medahatte', name: 'Medahatte' },
      { id: 'tinde', name: 'Tindé' },
      { id: 'urar-lkhalat', name: 'Urar lkhalat' },
      { id: 'fkirette', name: 'Fkirette' },
    ],
    'musiques-populaires-vivantes': [
      { id: 'rai', name: 'Raï' },
      { id: 'chaabi', name: 'Chaabi' },
      { id: 'alaoui', name: 'Alaoui' },
      { id: 'tichoumaren', name: 'Tichoumaren' },
      { id: 'staifi', name: 'Staïfi' },
      { id: 'rap', name: 'Rap' },
      { id: 'slam', name: 'Slam' },
    ],
    'musiques-regionales': [
      { id: 'haydouss', name: 'Haydouss' },
      { id: 'foundou', name: 'Foundou' },
      { id: 'ah-lelil', name: 'Ah lelil' },
      { id: 'gwarir', name: 'Gwarir' },
      { id: 'nhari', name: 'Nhari' },
      { id: 'el-asri', name: 'El asri' },
      { id: 'bedoui', name: 'Bedoui' },
      { id: 'karkabou', name: 'Karkabou' },
      { id: 'idballen', name: 'Idballen' },
      { id: 'rakrouki', name: 'Rakrouki' },
      { id: 'el-houl', name: 'El Houl' },
    ],
  };

  // Fonction pour compter les éléments dans chaque catégorie de vêtements
  const getTraditionCategoryCount = (categoryId: string): number => {
    const items = traditionItems[categoryId] || [];
    let count = items.length;
    
    // Ajouter les sous-éléments pour costume-feminin
    if (categoryId === 'costume-feminin') {
      Object.values(costumeFemininItems).forEach(subItems => {
        count += subItems.length;
      });
    }
    // Ajouter les sous-éléments pour costume-masculin
    if (categoryId === 'costume-masculin') {
      Object.values(costumeMasculinItems).forEach(subItems => {
        count += subItems.length;
      });
    }
    // Ajouter les sous-éléments pour bijoux-accessoires
    if (categoryId === 'bijoux-accessoires') {
      Object.values(bijouxItems).forEach(subItems => {
        count += subItems.length;
      });
    }
    // Ajouter les sous-éléments pour tenues-disparues
    if (categoryId === 'tenues-disparues') {
      Object.values(tenuesDisparuesItems).forEach(subItems => {
        count += subItems.length;
      });
    }
    
    return count;
  };

  // Fonction pour compter les éléments dans chaque catégorie d'artisanat
  const getArtisanatCategoryCount = (categoryId: string): number => {
    return artisanatItems[categoryId]?.length || 0;
  };

  // Fonction pour compter les éléments dans chaque catégorie de musique
  const getMusiqueCategoryCount = (categoryId: string): number => {
    const items = musiqueItems[categoryId] || [];
    let count = items.length;
    
    // Ajouter les instruments pour instruments-traditionnels
    if (categoryId === 'instruments-traditionnels') {
      Object.values(instrumentsItems).forEach(subItems => {
        count += subItems.length;
      });
    }
    
    // Ajouter les sous-éléments pour musiques-chants
    if (categoryId === 'musiques-chants') {
      Object.values(musiquesChantSubItems).forEach(subItems => {
        count += subItems.length;
      });
    }
    
    return count;
  };

  const handleTraditionCategoryClick = (categoryId: string) => {
    if (selectedTraditionCategory === categoryId) {
      return;
    }
    setSelectedTraditionCategory(categoryId);
    setSelectedCostumeFemininCategory(null);
    setSelectedCostumeMasculinCategory(null);
    setSelectedBijouxCategory(null);
    setSelectedTenuesDisparuesCategory(null);
    if (traditionItemsSwiperRef.current) {
      traditionItemsSwiperRef.current.slideTo(0);
    }
  };

  const handleArtisanatCategoryClick = (categoryId: string) => {
    if (selectedArtisanatCategory === categoryId) {
      return;
    }
    setSelectedArtisanatCategory(categoryId);
    if (artisanatItemsSwiperRef.current) {
      artisanatItemsSwiperRef.current.slideTo(0);
    }
  };

  const handleMusiqueCategoryClick = (categoryId: string) => {
    if (selectedMusiqueCategory === categoryId) {
      return;
    }
    setSelectedMusiqueCategory(categoryId);
    setSelectedInstrumentsSubcategory(null); // Reset subcategory when changing category
    setSelectedMusiquesChantSubcategory(null); // Reset musiques-chants subcategory
    if (itemsSwiperRef.current) {
      itemsSwiperRef.current.slideTo(0);
    }
    if (instrumentsSubcategorySwiperRef.current) {
      instrumentsSubcategorySwiperRef.current.slideTo(0);
    }
  };

  const handleInstrumentsSubcategoryClick = (subcategoryId: string) => {
    if (selectedInstrumentsSubcategory === subcategoryId) {
      return;
    }
    setSelectedInstrumentsSubcategory(subcategoryId);
    if (instrumentsItemsSwiperRef.current) {
      instrumentsItemsSwiperRef.current.slideTo(0);
    }
  };

  const getCurrentTraditionItems = (): Array<{ id: string; name: string; image?: string }> => {
    if (!selectedTraditionCategory) return [];
    if (selectedTraditionCategory === 'costume-feminin' && selectedCostumeFemininCategory) {
      return costumeFemininItems[selectedCostumeFemininCategory] || [];
    }
    if (selectedTraditionCategory === 'costume-masculin' && selectedCostumeMasculinCategory) {
      return costumeMasculinItems[selectedCostumeMasculinCategory] || [];
    }
    if (selectedTraditionCategory === 'bijoux-accessoires' && selectedBijouxCategory) {
      return bijouxItems[selectedBijouxCategory] || [];
    }
    if (selectedTraditionCategory === 'tenues-disparues' && selectedTenuesDisparuesCategory) {
      return tenuesDisparuesItems[selectedTenuesDisparuesCategory] || [];
    }
    return traditionItems[selectedTraditionCategory] || [];
  };

  const getCurrentArtisanatItems = () => {
    if (!selectedArtisanatCategory) return [];
    return artisanatItems[selectedArtisanatCategory] || [];
  };

  const getCurrentItems = () => {
    if (!selectedMusiqueCategory) return [];
    // Si c'est instruments-traditionnels, on gère différemment
    if (selectedMusiqueCategory === 'instruments-traditionnels') {
      return [];
    }
    return musiqueItems[selectedMusiqueCategory] || [];
  };

  const getCurrentInstrumentsItems = (): Array<{ id: string; name: string; description: string; image?: string }> => {
    if (!selectedInstrumentsSubcategory) return [];
    return instrumentsItems[selectedInstrumentsSubcategory] || [];
  };

  // Fonction pour obtenir l'URL complète de l'image
  const getImageUrl = (imagePath: string | undefined): string => {
    if (!imagePath) return '';
    // En développement, utiliser l'URL complète du backend pour éviter les problèmes de proxy
    if (process.env.NODE_ENV === 'development') {
      return `http://localhost:5001${imagePath}`;
    }
    return imagePath;
  };

  const getCurrentTraditionCategory = () => {
    return traditionCategories.find(cat => cat.id === selectedTraditionCategory);
  };

  const getCurrentArtisanatCategory = () => {
    return artisanatCategories.find(cat => cat.id === selectedArtisanatCategory);
  };

  const getCurrentMusiqueCategory = () => {
    return musiqueCategories.find(cat => cat.id === selectedMusiqueCategory);
  };

  const getTraditionCategoryColor = (categoryId: string): string => {
    const category = traditionCategories.find(cat => cat.id === categoryId);
    return category?.color || theme.colors.outchou_green;
  };

  const getArtisanatCategoryColor = (categoryId: string): string => {
    const category = artisanatCategories.find(cat => cat.id === categoryId);
    return category?.color || theme.colors.outchou_green;
  };

  const getCategoryColor = (categoryId: string): string => {
    const category = musiqueCategories.find(cat => cat.id === categoryId);
    return category?.color || theme.colors.outchou_green;
  };
  
  return (
    <SharedPageLayout 
              backgroundImage="/shared_assets/images/navigation/tradition.jpeg"
      pageTitle={traditionData?.label}
      pageDescription={traditionData?.page_text}
    >
      <TraditionMainSection>
        <TraditionMainContent>
          <ConstructionBanner>Page en construction</ConstructionBanner>
          {/* Titre du carrousel */}
          <div style={{ marginBottom: '0', width: '100%' }}>
            <SectionTitleWrapper>
              <SectionTitle>
                Vêtements & apparat
              </SectionTitle>
              <ScrollIndicator>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </ScrollIndicator>
            </SectionTitleWrapper>
          </div>

          {/* Section des items avec carrousel - AFFICHÉE QUAND UNE CATÉGORIE EST SÉLECTIONNÉE */}
          {selectedTraditionCategory && (
            <ItemsSection>
              {/* Breadcrumb pour la navigation */}
              <Breadcrumb>
                <BreadcrumbItem 
                  $isClickable={true}
                  onClick={() => {
                    setSelectedTraditionCategory(null);
                    setSelectedCostumeFemininCategory(null);
                    setSelectedCostumeMasculinCategory(null);
                    setSelectedBijouxCategory(null);
                    setSelectedTenuesDisparuesCategory(null);
                  }}
                >
                  Vêtements & apparat
                </BreadcrumbItem>
                <BreadcrumbSeparator>›</BreadcrumbSeparator>
                <BreadcrumbItem
                  $isClickable={
                    (selectedTraditionCategory === 'costume-feminin' && !!selectedCostumeFemininCategory) ||
                    (selectedTraditionCategory === 'costume-masculin' && !!selectedCostumeMasculinCategory) ||
                    (selectedTraditionCategory === 'bijoux-accessoires' && !!selectedBijouxCategory) ||
                    (selectedTraditionCategory === 'tenues-disparues' && !!selectedTenuesDisparuesCategory)
                  }
                  onClick={() => {
                    if (selectedTraditionCategory === 'costume-feminin' && selectedCostumeFemininCategory) {
                      setSelectedCostumeFemininCategory(null);
                      traditionItemsSwiperRef.current?.slideTo(0);
                    }
                    if (selectedTraditionCategory === 'costume-masculin' && selectedCostumeMasculinCategory) {
                      setSelectedCostumeMasculinCategory(null);
                      traditionItemsSwiperRef.current?.slideTo(0);
                    }
                    if (selectedTraditionCategory === 'bijoux-accessoires' && selectedBijouxCategory) {
                      setSelectedBijouxCategory(null);
                      traditionItemsSwiperRef.current?.slideTo(0);
                    }
                    if (selectedTraditionCategory === 'tenues-disparues' && selectedTenuesDisparuesCategory) {
                      setSelectedTenuesDisparuesCategory(null);
                      traditionItemsSwiperRef.current?.slideTo(0);
                    }
                  }}
                >
                  {getCurrentTraditionCategory()?.name}
                </BreadcrumbItem>
                {selectedTraditionCategory === 'costume-feminin' && selectedCostumeFemininCategory && (
                  <>
                    <BreadcrumbSeparator>›</BreadcrumbSeparator>
                    <BreadcrumbItem $isClickable={false}>
                      {traditionItems['costume-feminin']?.find(i => i.id === selectedCostumeFemininCategory)?.name || 'Robes'}
                    </BreadcrumbItem>
                  </>
                )}
                {selectedTraditionCategory === 'costume-masculin' && selectedCostumeMasculinCategory && (
                  <>
                    <BreadcrumbSeparator>›</BreadcrumbSeparator>
                    <BreadcrumbItem $isClickable={false}>
                      {traditionItems['costume-masculin']?.find(i => i.id === selectedCostumeMasculinCategory)?.name || 'Robes'}
                    </BreadcrumbItem>
                  </>
                )}
                {selectedTraditionCategory === 'bijoux-accessoires' && selectedBijouxCategory && (
                  <>
                    <BreadcrumbSeparator>›</BreadcrumbSeparator>
                    <BreadcrumbItem $isClickable={false}>
                      {traditionItems['bijoux-accessoires']?.find(i => i.id === selectedBijouxCategory)?.name || 'Collier'}
                    </BreadcrumbItem>
                  </>
                )}
                {selectedTraditionCategory === 'tenues-disparues' && selectedTenuesDisparuesCategory && (
                  <>
                    <BreadcrumbSeparator>›</BreadcrumbSeparator>
                    <BreadcrumbItem $isClickable={false}>
                      {traditionItems['tenues-disparues']?.find(i => i.id === selectedTenuesDisparuesCategory)?.name || 'Tenues féminines'}
                    </BreadcrumbItem>
                  </>
                )}
              </Breadcrumb>

              {selectedTraditionCategory === 'bijoux-accessoires' && (
                <CategoryNote>
                  La bijouterie traditionnelle algérienne repose historiquement sur l'orfèvrerie d'argent, largement diffusée dans les sociétés rurales et nomades pour sa valeur symbolique, sociale et économique. Dans les régions sahariennes et sahélo-sahariennes, les parures associent souvent l'argent à des cauris, des perles colorées ou des matériaux organiques, témoignant des échanges anciens avec l'Afrique subsaharienne.
                  <br />
                  À partir du 20ᵉ siècle, l'urbanisation et les transformations sociales favorisent une diffusion croissante des bijoux en or, perçus comme réserve économique et symbole de statut, sans faire disparaître les traditions régionales plus anciennes.
                </CategoryNote>
              )}

              {selectedTraditionCategory === 'costume-masculin' && (
                <CategoryNote>
                  Le costume masculin algérien combine des vêtements communs à plusieurs régions et des variantes locales adaptées au climat et au mode de vie. La robe longue ou gandoura est portée dans tout le pays, tandis que le burnous, très présent dans le Tell et les Hauts-Plateaux, devient un symbole d'élégance et d'honneur, avec des versions plus légères dans certaines régions du Sud. Dans les villes, surtout durant la période ottomane, l'habillement masculin s'enrichit de vestes, gilets et caftans, illustrant un style plus citadin et raffiné.
                </CategoryNote>
              )}

              {selectedTraditionCategory === 'costume-feminin' && (
                <CategoryNote>
                  Le costume féminin algérien s'appuie sur une base très ancienne d'un vêtement drapé, proche du péplos antique, dont chaque région propose sa version. Les tenues cousues, en velours et brodées au fil d'or, sont présentes depuis environ deux siècles dans plusieurs centres urbains, notamment à Constantine, à Alger, puis diffusées vers d'autres régions. Aujourd'hui, ces vêtements continuent d'évoluer : certaines broderies traditionnelles au fil d'or tendent à être remplacées par le perlage, tout en conservant leur rôle symbolique et culturel.
                </CategoryNote>
              )}

              {selectedTraditionCategory === 'tenues-disparues' && (
                <CategoryNote>
                  Les éléments vestimentaires traditionnels algériens ont progressivement disparu sous l'effet de la colonisation et de la mondialisation, qui ont introduit de nouveaux modes vestimentaires et transformé les usages quotidiens. Certaines tenues ont quitté la vie courante pour devenir uniquement cérémonielles, tandis que les bijoux en argent ont souvent été remplacés par l'or et que les bijoux de tête ont décliné avec l'abandon des coiffes traditionnelles. La transformation des modes de vie a aussi entraîné la disparition d'accessoires utilitaires, notamment dans le Sahara, ainsi que le recul des chaussures traditionnelles face aux productions industrielles.
                </CategoryNote>
              )}
              
              <ItemsCarouselWrapper $categoryColor={selectedTraditionCategory ? getTraditionCategoryColor(selectedTraditionCategory) : undefined}>
                <Swiper
                  modules={[Navigation, FreeMode]}
                  spaceBetween={20}
                  slidesPerView="auto"
                  freeMode={true}
                  navigation={false}
                  onSwiper={(swiper: SwiperType) => {
                    traditionItemsSwiperRef.current = swiper;
                    setUpdateTrigger(prev => prev + 1);
                  }}
                  onSlideChange={() => {
                    setUpdateTrigger(prev => prev + 1);
                  }}
                >
                  {getCurrentTraditionItems().map((item, index) => (
                    <SwiperSlide key={`${selectedTraditionCategory}-${item.id}-${index}`} style={{ width: 'auto' }}>
                      <ItemCard
                        onClick={() => {
                          // 2ᵉ niveau uniquement pour Costume féminin -> (Robes, Tenues complètes)
                          if (
                            selectedTraditionCategory === 'costume-feminin' &&
                            !selectedCostumeFemininCategory &&
                            (
                              item.id === 'robes' ||
                              item.id === 'tenues-completes' ||
                              item.id === 'caftans' ||
                              item.id === 'tuniques' ||
                              item.id === 'pantalons' ||
                              item.id === 'vestes-pieces-dessus' ||
                              item.id === 'coiffes-parures-tete'
                            )
                          ) {
                            setSelectedCostumeFemininCategory(item.id);
                            traditionItemsSwiperRef.current?.slideTo(0);
                            return;
                          }
                          // 2ᵉ niveau pour Costume masculin
                          if (
                            selectedTraditionCategory === 'costume-masculin' &&
                            !selectedCostumeMasculinCategory &&
                            (
                              item.id === 'robes' ||
                              item.id === 'couvre-chef' ||
                              item.id === 'vestes-pieces-dessus' ||
                              item.id === 'pantalon'
                            )
                          ) {
                            setSelectedCostumeMasculinCategory(item.id);
                            traditionItemsSwiperRef.current?.slideTo(0);
                            return;
                          }
                          // 2ᵉ niveau pour Bijoux & accessoires -> Bijoux de tête, Collier, Boucles d'oreilles, Bracelet, Bijoux de chevilles ou Autres
                          if (
                            selectedTraditionCategory === 'bijoux-accessoires' &&
                            !selectedBijouxCategory &&
                            (item.id === 'fibule' || item.id === 'collier' || item.id === 'boucles-oreilles' || item.id === 'bracelet' || item.id === 'bijoux-chevilles' || item.id === 'autres')
                          ) {
                            setSelectedBijouxCategory(item.id);
                            traditionItemsSwiperRef.current?.slideTo(0);
                            return;
                          }
                          // 2ᵉ niveau pour Éléments disparus -> Tenues féminines, Tenues masculines, Bijoux, Accessoires ou Chaussures
                          if (
                            selectedTraditionCategory === 'tenues-disparues' &&
                            !selectedTenuesDisparuesCategory &&
                            (item.id === 'tenues-feminines' || item.id === 'tenues-masculines' || item.id === 'bijoux' || item.id === 'accessoires' || item.id === 'chaussures')
                          ) {
                            setSelectedTenuesDisparuesCategory(item.id);
                            traditionItemsSwiperRef.current?.slideTo(0);
                            return;
                          }
                          // Si aucune sous-catégorie n'est ouverte, ouvrir la pop-up
                          setSelectedItem(item as TraditionItem);
                          setSelectedItemColor(getTraditionCategoryColor(selectedTraditionCategory) || theme.colors.outchou_green);
                        }}
                      >
                        <ItemImage 
                          imageUrl={item.image}
                          $categoryColor={getTraditionCategoryColor(selectedTraditionCategory)}
                        >
                          <ItemContent>
                            <ItemName>{item.name}</ItemName>
                          </ItemContent>
                        </ItemImage>
                      </ItemCard>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <ItemsNavigationButtons $categoryColor={selectedTraditionCategory ? getTraditionCategoryColor(selectedTraditionCategory) : undefined}>
                  <ItemNavButton
                    $categoryColor={selectedTraditionCategory ? getTraditionCategoryColor(selectedTraditionCategory) : undefined}
                    $disabled={!traditionItemsSwiperRef.current || traditionItemsSwiperRef.current.isBeginning}
                    onClick={() => traditionItemsSwiperRef.current?.slidePrev()}
                  >
                    ‹
                  </ItemNavButton>
                  <ItemNavButton
                    $categoryColor={selectedTraditionCategory ? getTraditionCategoryColor(selectedTraditionCategory) : undefined}
                    $disabled={!traditionItemsSwiperRef.current || traditionItemsSwiperRef.current.isEnd}
                    onClick={() => traditionItemsSwiperRef.current?.slideNext()}
                  >
                    ›
                  </ItemNavButton>
                </ItemsNavigationButtons>
              </ItemsCarouselWrapper>
            </ItemsSection>
          )}

          {/* Carrousel des catégories - AFFICHÉ SEULEMENT SI AUCUNE CATÉGORIE N'EST SÉLECTIONNÉE */}
          {!selectedTraditionCategory && (
            <CategoriesCarouselWrapper>
              <Swiper
                modules={[Navigation, FreeMode]}
                spaceBetween={20}
                slidesPerView="auto"
                freeMode={true}
                navigation={false}
                onSwiper={(swiper: SwiperType) => {
                  categorySwiperRef.current = swiper;
                  setUpdateTrigger(prev => prev + 1);
                }}
                onSlideChange={() => {
                  setUpdateTrigger(prev => prev + 1);
                }}
              >
                {traditionCategories.map((category) => (
                  <SwiperSlide key={category.id} style={{ width: 'auto' }}>
                    <CategoryCard onClick={() => handleTraditionCategoryClick(category.id)}>
                      <CategoryImage $categoryColor={category.color} imageUrl={category.image}>
                        <CategoryContent>
                          <CategoryTitle>{category.name}</CategoryTitle>
                          <CategoryCount>
                            {getTraditionCategoryCount(category.id)} élément{getTraditionCategoryCount(category.id) > 1 ? 's' : ''}
                          </CategoryCount>
                        </CategoryContent>
                      </CategoryImage>
                    </CategoryCard>
                  </SwiperSlide>
                ))}
              </Swiper>
              <CategoryNavigationButtons>
                <CategoryNavButton
                  $disabled={!categorySwiperRef.current || categorySwiperRef.current.isBeginning}
                  onClick={() => categorySwiperRef.current?.slidePrev()}
                >
                  ‹
                </CategoryNavButton>
                <CategoryNavButton
                  $disabled={!categorySwiperRef.current || categorySwiperRef.current.isEnd}
                  onClick={() => categorySwiperRef.current?.slideNext()}
                >
                  ›
                </CategoryNavButton>
              </CategoryNavigationButtons>
            </CategoriesCarouselWrapper>
          )}

          {/* Titre du deuxième carrousel */}
          <div style={{ marginBottom: '0', width: '100%', marginTop: '60px' }}>
            <SectionTitleWrapper>
              <SectionTitle>
                Artisanat & savoir-faire
              </SectionTitle>
              <ScrollIndicator>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </ScrollIndicator>
            </SectionTitleWrapper>
          </div>

          {/* Section des items avec carrousel - AFFICHÉE QUAND UNE CATÉGORIE EST SÉLECTIONNÉE */}
          {selectedArtisanatCategory && (
            <ItemsSection>
              {/* Breadcrumb pour la navigation */}
              <Breadcrumb>
                <BreadcrumbItem 
                  $isClickable={true}
                  onClick={() => {
                    setSelectedArtisanatCategory(null);
                  }}
                >
                  Artisanat & savoir-faire
                </BreadcrumbItem>
                <BreadcrumbSeparator>›</BreadcrumbSeparator>
                <BreadcrumbItem $isClickable={false}>
                  {getCurrentArtisanatCategory()?.name}
                </BreadcrumbItem>
              </Breadcrumb>

              {selectedArtisanatCategory === 'tapisserie' && (
                <CategoryNote>
                  Les tapis traditionnels algériens se distinguent par leur variété en matières, motifs, couleurs et techniques, chacune étroitement liée aux territoires et aux groupes humains qui les produisent.
                  <br />
                  Le tapis n'est pas seulement un objet décoratif : il est utilitaire (couvre-sol, isolant, literie), social (marque d'identité tribale, d'appartenance) et symbolique (motifs protecteurs ou spirituels).
                </CategoryNote>
              )}

              {selectedArtisanatCategory === 'travail-metal' && (
                <CategoryNote>
                  La dinanderie, art du travail du cuivre et des métaux associés, occupe une place majeure dans l'artisanat algérien et reflète des siècles de savoir-faire, d'échanges et d'évolution stylistique.
                  <br />
                  Elle existe en Algérie depuis le Moyen Âge, s'épanouissant grâce à la richesse du sous-sol algérien en cuivre et aux influences successives des peuples andalous, italiens, ottomans, persans et autochtones. Entre le XVe et le XVIIIe siècles, l'arrivée d'artisans d'Andalousie, de Livourne (Italie) et de Turquie a profondément enrichi cet art, combinant techniques locales et motifs orientaux pour donner naissance à des objets à la fois utilitaires et décoratifs.
                </CategoryNote>
              )}

              {selectedArtisanatCategory === 'poterie' && (
                <CategoryNote>
                  La poterie algérienne est un patrimoine artisanal ancestral, témoignage d'une culture riche et diversifiée. Elle remonte au Néolithique, soit près de 1000 ans avant notre ère, notamment en Kabylie, et s'est enrichie au fil des siècles grâce aux influences phéniciennes, romaines, andalouses et ottomanes. Chaque région du pays développe un style unique, adapté aux usages locaux, aux matériaux disponibles et aux traditions sociales.
                </CategoryNote>
              )}

              {selectedArtisanatCategory === 'fibres-tressage' && (
                <CategoryNote>
                  La vannerie en Algérie remonte à la préhistoire, avec des vestiges découverts datant du Néolithique (entre 5500 et 4500 avant J.-C.). Elle a perduré durant l'époque carthaginoise, romaine (ex : mosaïques à Oum El Bouaghi présentant des paniers), arabo-islamique, ottomane, et jusqu'à l'époque contemporaine. Ce perpétuel développement souligne son importance dans la vie quotidienne traditionnelle algérienne.
                </CategoryNote>
              )}

              {selectedArtisanatCategory === 'broderie' && (
                <CategoryNote>
                  La broderie telle qu'on la connaît en Algérie puise ses racines dans les grandes traditions textiles du bassin méditerranéen et du Proche-Orient. L'influence orientale, notamment syrienne et byzantine, s'illustre dans le développement de techniques raffinées, souvent transmises par les routes commerciales et les déplacements de populations lors des différentes périodes impériales, surtout à partir de la période ottomane (XVIe-XIXe siècle). Sous les Ottomans, Alger devient un foyer majeur d'innovation textile, assimilant et adaptant motifs et savoir-faire venus d'Alep, de Damas et d'Istanbul. Les échanges avec l'Andalousie et la Méditerranée enrichissent le répertoire décoratif local, intégrant des apports arabes, amazigh, mais aussi européens.
                </CategoryNote>
              )}

              {selectedArtisanatCategory === 'cuir-maroquinerie' && (
                <CategoryNote>
                  La maroquinerie en Algérie remonte aux sociétés nomades antiques, où les peaux servaient pour les vêtements, équipements et montures. Avec les échanges commerciaux transsahariens et méditerranéens, la maroquinerie s'est professionnalisée dans des villes comme Alger, Constantine et Tlemcen.
                </CategoryNote>
              )}

              {selectedArtisanatCategory === 'bois' && (
                <CategoryNote>
                  L'ébénisterie algérienne est un art ancien qui consiste à travailler le bois pour créer meubles, coffres, portes et objets décoratifs. Elle reflète un patrimoine riche et diversifié, mêlant influences islamiques, ottomanes, andalouses, méditerranéennes et amazigh locales.
                  <br />
                  Au fil des siècles, trois grands courants se distinguent : l'ébénisterie damasquinée, l'ébénisterie polychrome et l'ébénisterie rurale sobre, chacune avec ses spécificités techniques, esthétiques et régionales.
                </CategoryNote>
              )}

              {selectedArtisanatCategory === 'céramique' && (
                <CategoryNote>
                  La céramique algérienne regroupe les objets en argile cuits à haute température et décorés d'émaux colorés, une technique développée dès le Moyen Âge autour de la Qalʿa des Béni Hammad puis perfectionnée dans des centres comme le Palais du Méchouar à Tlemcen. Influencée par les échanges méditerranéens, notamment avec les ateliers de Qallaline à Tunis, elle se reconnaît par ses couleurs emblématiques (bleu, vert, jaune, blanc) et ses motifs géométriques ou floraux caractéristiques du patrimoine artistique algérien.
                </CategoryNote>
              )}
              
              <ItemsCarouselWrapper $categoryColor={selectedArtisanatCategory ? getArtisanatCategoryColor(selectedArtisanatCategory) : undefined}>
                <Swiper
                  modules={[Navigation, FreeMode]}
                  spaceBetween={20}
                  slidesPerView="auto"
                  freeMode={true}
                  navigation={false}
                  onSwiper={(swiper: SwiperType) => {
                    artisanatItemsSwiperRef.current = swiper;
                    setUpdateTrigger(prev => prev + 1);
                  }}
                  onSlideChange={() => {
                    setUpdateTrigger(prev => prev + 1);
                  }}
                >
                  {getCurrentArtisanatItems().map((item, index) => (
                    <SwiperSlide key={`${selectedArtisanatCategory}-${item.id}-${index}`} style={{ width: 'auto' }}>
                      <ItemCard onClick={() => {
                        setSelectedItem(item as TraditionItem);
                        setSelectedItemColor(getArtisanatCategoryColor(selectedArtisanatCategory) || theme.colors.outchou_green);
                      }}>
                        <ItemImage 
                          imageUrl={item.image}
                          $categoryColor={getArtisanatCategoryColor(selectedArtisanatCategory)}
                        >
                          <ItemContent>
                            <ItemName>{item.name}</ItemName>
                          </ItemContent>
                        </ItemImage>
                      </ItemCard>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <ItemsNavigationButtons $categoryColor={selectedArtisanatCategory ? getArtisanatCategoryColor(selectedArtisanatCategory) : undefined}>
                  <ItemNavButton
                    $categoryColor={selectedArtisanatCategory ? getArtisanatCategoryColor(selectedArtisanatCategory) : undefined}
                    $disabled={!artisanatItemsSwiperRef.current || artisanatItemsSwiperRef.current.isBeginning}
                    onClick={() => artisanatItemsSwiperRef.current?.slidePrev()}
                  >
                    ‹
                  </ItemNavButton>
                  <ItemNavButton
                    $categoryColor={selectedArtisanatCategory ? getArtisanatCategoryColor(selectedArtisanatCategory) : undefined}
                    $disabled={!artisanatItemsSwiperRef.current || artisanatItemsSwiperRef.current.isEnd}
                    onClick={() => artisanatItemsSwiperRef.current?.slideNext()}
                  >
                    ›
                  </ItemNavButton>
                </ItemsNavigationButtons>
              </ItemsCarouselWrapper>
            </ItemsSection>
          )}

          {/* Carrousel de l'artisanat - AFFICHÉ SEULEMENT SI AUCUNE CATÉGORIE N'EST SÉLECTIONNÉE */}
          {!selectedArtisanatCategory && (
            <CategoriesCarouselWrapper>
              <Swiper
                modules={[Navigation, FreeMode]}
                spaceBetween={20}
                slidesPerView="auto"
                freeMode={true}
                navigation={false}
                onSwiper={(swiper: SwiperType) => {
                  artisanatSwiperRef.current = swiper;
                  setUpdateTrigger(prev => prev + 1);
                }}
                onSlideChange={() => {
                  setUpdateTrigger(prev => prev + 1);
                }}
              >
                {artisanatCategories.map((category) => (
                  <SwiperSlide key={category.id} style={{ width: 'auto' }}>
                    <CategoryCard onClick={() => handleArtisanatCategoryClick(category.id)}>
                        <CategoryImage $categoryColor={category.color} imageUrl={category.image}>
                        <CategoryContent>
                          <CategoryTitle>{category.name}</CategoryTitle>
                          <CategoryCount>
                            {getArtisanatCategoryCount(category.id)} élément{getArtisanatCategoryCount(category.id) > 1 ? 's' : ''}
                          </CategoryCount>
                        </CategoryContent>
                      </CategoryImage>
                    </CategoryCard>
                  </SwiperSlide>
                ))}
              </Swiper>
              <CategoryNavigationButtons>
                <CategoryNavButton
                  $disabled={!artisanatSwiperRef.current || artisanatSwiperRef.current.isBeginning}
                  onClick={() => artisanatSwiperRef.current?.slidePrev()}
                >
                  ‹
                </CategoryNavButton>
                <CategoryNavButton
                  $disabled={!artisanatSwiperRef.current || artisanatSwiperRef.current.isEnd}
                  onClick={() => artisanatSwiperRef.current?.slideNext()}
                >
                  ›
                </CategoryNavButton>
              </CategoryNavigationButtons>
            </CategoriesCarouselWrapper>
          )}

          {/* Titre du troisième carrousel */}
          <div style={{ marginBottom: '0', width: '100%', marginTop: '60px' }}>
            <SectionTitleWrapper>
              <SectionTitle>
                Musique & danse
              </SectionTitle>
              <ScrollIndicator>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </ScrollIndicator>
            </SectionTitleWrapper>
          </div>

          {/* Section spéciale pour Instruments traditionnels avec sous-catégories */}
          {selectedMusiqueCategory === 'instruments-traditionnels' && !selectedInstrumentsSubcategory && (
            <ItemsSection>
              {/* Breadcrumb pour la navigation */}
              <Breadcrumb>
                <BreadcrumbItem 
                  $isClickable={true}
                  onClick={() => {
                    setSelectedMusiqueCategory(null);
                    setSelectedInstrumentsSubcategory(null);
                  }}
                >
                  Musique & danse
                </BreadcrumbItem>
                <BreadcrumbSeparator>›</BreadcrumbSeparator>
                <BreadcrumbItem $isClickable={false}>
                  Instruments traditionnels
                </BreadcrumbItem>
              </Breadcrumb>
              
              <ItemsCarouselWrapper $categoryColor={getCategoryColor('instruments-traditionnels')}>
                <Swiper
                  modules={[Navigation, FreeMode]}
                  spaceBetween={20}
                  slidesPerView="auto"
                  freeMode={true}
                  navigation={false}
                  onSwiper={(swiper: SwiperType) => {
                    instrumentsSubcategorySwiperRef.current = swiper;
                    setUpdateTrigger(prev => prev + 1);
                  }}
                  onSlideChange={() => {
                    setUpdateTrigger(prev => prev + 1);
                  }}
                >
                  {instrumentsSubcategories.map((subcategory) => (
                    <SwiperSlide key={subcategory.id} style={{ width: 'auto' }}>
                      <CategoryCard onClick={() => handleInstrumentsSubcategoryClick(subcategory.id)}>
                        <CategoryImage $categoryColor={subcategory.color} imageUrl={subcategory.image}>
                          <CategoryContent>
                            <CategoryTitle>{subcategory.name}</CategoryTitle>
                          </CategoryContent>
                        </CategoryImage>
                      </CategoryCard>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <ItemsNavigationButtons $categoryColor={getCategoryColor('instruments-traditionnels')}>
                  <ItemNavButton
                    $categoryColor={getCategoryColor('instruments-traditionnels')}
                    $disabled={!instrumentsSubcategorySwiperRef.current || instrumentsSubcategorySwiperRef.current.isBeginning}
                    onClick={() => instrumentsSubcategorySwiperRef.current?.slidePrev()}
                  >
                    ‹
                  </ItemNavButton>
                  <ItemNavButton
                    $categoryColor={getCategoryColor('instruments-traditionnels')}
                    $disabled={!instrumentsSubcategorySwiperRef.current || instrumentsSubcategorySwiperRef.current.isEnd}
                    onClick={() => instrumentsSubcategorySwiperRef.current?.slideNext()}
                  >
                    ›
                  </ItemNavButton>
                </ItemsNavigationButtons>
              </ItemsCarouselWrapper>
            </ItemsSection>
          )}

          {/* Section des instruments avec descriptions - AFFICHÉE QUAND UNE SOUS-CATÉGORIE D'INSTRUMENTS EST SÉLECTIONNÉE */}
          {selectedMusiqueCategory === 'instruments-traditionnels' && selectedInstrumentsSubcategory && (
            <ItemsSection>
              {/* Breadcrumb pour la navigation */}
              <Breadcrumb>
                <BreadcrumbItem 
                  $isClickable={true}
                  onClick={() => {
                    setSelectedMusiqueCategory(null);
                    setSelectedInstrumentsSubcategory(null);
                  }}
                >
                  Musique & danse
                </BreadcrumbItem>
                <BreadcrumbSeparator>›</BreadcrumbSeparator>
                <BreadcrumbItem 
                  $isClickable={true}
                  onClick={() => {
                    setSelectedInstrumentsSubcategory(null);
                  }}
                >
                  Instruments traditionnels
                </BreadcrumbItem>
                <BreadcrumbSeparator>›</BreadcrumbSeparator>
                <BreadcrumbItem $isClickable={false}>
                  {instrumentsSubcategories.find(sub => sub.id === selectedInstrumentsSubcategory)?.name}
                </BreadcrumbItem>
              </Breadcrumb>

              <CategoryNote>
                La musique traditionnelle algérienne utilise une grande variété d'instruments, répartis en trois grandes catégories : à cordes, à vent et à percussion. Beaucoup de ces instruments ont des origines anciennes, reflétant les échanges culturels entre le monde arabe, l'Afrique et la Méditerranée. Certains sont également spécifiques à certaines régions du pays.
              </CategoryNote>
              
              <ItemsCarouselWrapper $categoryColor={instrumentsSubcategories.find(sub => sub.id === selectedInstrumentsSubcategory)?.color}>
                <Swiper
                  modules={[Navigation, FreeMode]}
                  spaceBetween={20}
                  slidesPerView="auto"
                  freeMode={true}
                  navigation={false}
                  onSwiper={(swiper: SwiperType) => {
                    instrumentsItemsSwiperRef.current = swiper;
                    setUpdateTrigger(prev => prev + 1);
                  }}
                  onSlideChange={() => {
                    setUpdateTrigger(prev => prev + 1);
                  }}
                >
                  {getCurrentInstrumentsItems().map((item, index) => (
                    <SwiperSlide key={`${selectedInstrumentsSubcategory}-${item.id}-${index}`} style={{ width: 'auto' }}>
                      <ItemCard onClick={() => {
                        setSelectedItem({ ...item, image: getImageUrl(item.image), sections: item.description ? { 'Description': item.description } : undefined } as TraditionItem);
                        setSelectedItemColor(instrumentsSubcategories.find(sub => sub.id === selectedInstrumentsSubcategory)?.color || theme.colors.outchou_green);
                      }}>
                        <ItemImage 
                          imageUrl={getImageUrl(item.image)}
                          $categoryColor={instrumentsSubcategories.find(sub => sub.id === selectedInstrumentsSubcategory)?.color}
                        >
                          <ItemContent>
                            <ItemName>{item.name}</ItemName>
                          </ItemContent>
                        </ItemImage>
                      </ItemCard>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <ItemsNavigationButtons $categoryColor={instrumentsSubcategories.find(sub => sub.id === selectedInstrumentsSubcategory)?.color}>
                  <ItemNavButton
                    $categoryColor={instrumentsSubcategories.find(sub => sub.id === selectedInstrumentsSubcategory)?.color}
                    $disabled={!instrumentsItemsSwiperRef.current || instrumentsItemsSwiperRef.current.isBeginning}
                    onClick={() => instrumentsItemsSwiperRef.current?.slidePrev()}
                  >
                    ‹
                  </ItemNavButton>
                  <ItemNavButton
                    $categoryColor={instrumentsSubcategories.find(sub => sub.id === selectedInstrumentsSubcategory)?.color}
                    $disabled={!instrumentsItemsSwiperRef.current || instrumentsItemsSwiperRef.current.isEnd}
                    onClick={() => instrumentsItemsSwiperRef.current?.slideNext()}
                  >
                    ›
                  </ItemNavButton>
                </ItemsNavigationButtons>
              </ItemsCarouselWrapper>
            </ItemsSection>
          )}

          {/* Section des items avec carrousel - AFFICHÉE QUAND UNE CATÉGORIE EST SÉLECTIONNÉE (sauf instruments-traditionnels) */}
          {selectedMusiqueCategory && selectedMusiqueCategory !== 'instruments-traditionnels' && (
            <ItemsSection>
              {/* Breadcrumb pour la navigation */}
              <Breadcrumb>
                <BreadcrumbItem 
                  $isClickable={true}
                  onClick={() => {
                    setSelectedMusiqueCategory(null);
                    setSelectedMusiquesChantSubcategory(null);
                  }}
                >
                  Musique & danse
                </BreadcrumbItem>
                <BreadcrumbSeparator>›</BreadcrumbSeparator>
                <BreadcrumbItem 
                  $isClickable={selectedMusiqueCategory === 'musiques-chants' && !!selectedMusiquesChantSubcategory}
                  onClick={() => {
                    if (selectedMusiqueCategory === 'musiques-chants' && selectedMusiquesChantSubcategory) {
                      setSelectedMusiquesChantSubcategory(null);
                      itemsSwiperRef.current?.slideTo(0);
                    }
                  }}
                >
                  {getCurrentMusiqueCategory()?.name}
                </BreadcrumbItem>
                {selectedMusiqueCategory === 'musiques-chants' && selectedMusiquesChantSubcategory && (
                  <>
                    <BreadcrumbSeparator>›</BreadcrumbSeparator>
                    <BreadcrumbItem $isClickable={false}>
                      {musiqueItems['musiques-chants']?.find(i => i.id === selectedMusiquesChantSubcategory)?.name}
                    </BreadcrumbItem>
                  </>
                )}
              </Breadcrumb>

              {selectedMusiqueCategory === 'danses' && (
                <CategoryNote>
                  La danse en Algérie est une composante essentielle du patrimoine immatériel. Elle exprime autant la joie, l'identité culturelle, que la mémoire collective des peuples qui la pratiquent. Les danses algériennes varient selon les régions, oscillant entre formes festives, spirituelles et sociales.
                </CategoryNote>
              )}

              {selectedMusiqueCategory === 'musiques-chants' && (
                <CategoryNote>
                  La musique algérienne reflète la richesse et la diversité du pays. Chaque région — du littoral méditerranéen aux confins du Sahara — a forgé ses propres styles, instruments et rythmes, hérités d'histoires multiples.
                  <br />
                  Ce panorama propose un voyage à travers les grandes régions musicales d'Algérie.
                </CategoryNote>
              )}
              
              <ItemsCarouselWrapper $categoryColor={selectedMusiqueCategory ? getCategoryColor(selectedMusiqueCategory) : undefined}>
                <Swiper
                  modules={[Navigation, FreeMode]}
                  spaceBetween={20}
                  slidesPerView="auto"
                  freeMode={true}
                  navigation={false}
                  onSwiper={(swiper: SwiperType) => {
                    itemsSwiperRef.current = swiper;
                    setUpdateTrigger(prev => prev + 1);
                  }}
                  onSlideChange={() => {
                    setUpdateTrigger(prev => prev + 1);
                  }}
                >
                  {/* Afficher les sous-éléments si une sous-catégorie de musiques-chants est sélectionnée */}
                  {selectedMusiqueCategory === 'musiques-chants' && selectedMusiquesChantSubcategory ? (
                    (musiquesChantSubItems[selectedMusiquesChantSubcategory] || []).map((item, index) => (
                      <SwiperSlide key={`${selectedMusiquesChantSubcategory}-${item.id}-${index}`} style={{ width: 'auto' }}>
                      <ItemCard onClick={() => {
                        setSelectedItem(item as TraditionItem);
                        setSelectedItemColor(getCategoryColor(selectedMusiqueCategory) || theme.colors.outchou_green);
                      }}>
                        <ItemImage $categoryColor={getCategoryColor(selectedMusiqueCategory)}>
                          <ItemContent>
                            <ItemName>{item.name}</ItemName>
                          </ItemContent>
                        </ItemImage>
                      </ItemCard>
                    </SwiperSlide>
                    ))
                  ) : (
                    getCurrentItems().map((item, index) => (
                      <SwiperSlide key={`${selectedMusiqueCategory}-${item.id}-${index}`} style={{ width: 'auto' }}>
                        <ItemCard
                          onClick={() => {
                            // Clic sur "Chants féminin" pour afficher ses sous-éléments
                            if (
                              selectedMusiqueCategory === 'musiques-chants' &&
                              !selectedMusiquesChantSubcategory &&
                              musiquesChantSubItems[item.id]
                            ) {
                              setSelectedMusiquesChantSubcategory(item.id);
                              itemsSwiperRef.current?.slideTo(0);
                            }
                          }}
                        >
                          <ItemImage $categoryColor={getCategoryColor(selectedMusiqueCategory)}>
                            <ItemContent>
                              <ItemName>{item.name}</ItemName>
                            </ItemContent>
                          </ItemImage>
                        </ItemCard>
                      </SwiperSlide>
                    ))
                  )}
                </Swiper>
                <ItemsNavigationButtons $categoryColor={selectedMusiqueCategory ? getCategoryColor(selectedMusiqueCategory) : undefined}>
                  <ItemNavButton
                    $categoryColor={selectedMusiqueCategory ? getCategoryColor(selectedMusiqueCategory) : undefined}
                    $disabled={!itemsSwiperRef.current || itemsSwiperRef.current.isBeginning}
                    onClick={() => itemsSwiperRef.current?.slidePrev()}
                  >
                    ‹
                  </ItemNavButton>
                  <ItemNavButton
                    $categoryColor={selectedMusiqueCategory ? getCategoryColor(selectedMusiqueCategory) : undefined}
                    $disabled={!itemsSwiperRef.current || itemsSwiperRef.current.isEnd}
                    onClick={() => itemsSwiperRef.current?.slideNext()}
                  >
                    ›
                  </ItemNavButton>
                </ItemsNavigationButtons>
              </ItemsCarouselWrapper>
            </ItemsSection>
          )}

          {/* Carrousel de la musique et danse - AFFICHÉ SEULEMENT SI AUCUNE CATÉGORIE N'EST SÉLECTIONNÉE */}
          {!selectedMusiqueCategory && (
            <CategoriesCarouselWrapper>
              <Swiper
                modules={[Navigation, FreeMode]}
                spaceBetween={20}
                slidesPerView="auto"
                freeMode={true}
                navigation={false}
                onSwiper={(swiper: SwiperType) => {
                  musiqueSwiperRef.current = swiper;
                  setUpdateTrigger(prev => prev + 1);
                }}
                onSlideChange={() => {
                  setUpdateTrigger(prev => prev + 1);
                }}
              >
                {musiqueCategories.map((category) => (
                  <SwiperSlide key={category.id} style={{ width: 'auto' }}>
                    <CategoryCard onClick={() => handleMusiqueCategoryClick(category.id)}>
                        <CategoryImage $categoryColor={category.color} imageUrl={category.image}>
                        <CategoryContent>
                          <CategoryTitle>{category.name}</CategoryTitle>
                          <CategoryCount>
                            {getMusiqueCategoryCount(category.id)} élément{getMusiqueCategoryCount(category.id) > 1 ? 's' : ''}
                          </CategoryCount>
                        </CategoryContent>
                      </CategoryImage>
                    </CategoryCard>
                  </SwiperSlide>
                ))}
              </Swiper>
              <CategoryNavigationButtons>
                <CategoryNavButton
                  $disabled={!musiqueSwiperRef.current || musiqueSwiperRef.current.isBeginning}
                  onClick={() => musiqueSwiperRef.current?.slidePrev()}
                >
                  ‹
                </CategoryNavButton>
                <CategoryNavButton
                  $disabled={!musiqueSwiperRef.current || musiqueSwiperRef.current.isEnd}
                  onClick={() => musiqueSwiperRef.current?.slideNext()}
                >
                  ›
                </CategoryNavButton>
              </CategoryNavigationButtons>
            </CategoriesCarouselWrapper>
          )}
        </TraditionMainContent>
      </TraditionMainSection>

      {/* Modal pour afficher les détails d'un élément */}
      <SectionModal
        title={selectedItem?.name || ''}
        imageUrl={selectedItem?.image}
        sections={selectedItem?.sections || { 'Description': 'Contenu à venir...' }}
        color={selectedItemColor}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </SharedPageLayout>
  );
};

export default TraditionPage; 